import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, BookOpen, RefreshCw, Sparkles, Save, Lock, Users } from 'lucide-react';
import aiService from '../services/aiService';
import { useDiaryMutations, DIARY_QUERY_KEYS } from '../hooks/useDiary';
import { AIExplanationDisplay } from '../utils/aiExplanationFormatter';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import PaymentModal from './PaymentModal';
import analytics from '../services/analytics';
import './CardDrawing.css';

const SpreadDrawingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  // Get authentication context
  const { user, isAuthenticated } = useAuth();
  
  // Get translation context
  const { t, currentLanguage } = useLanguage();

  // Helper function to translate card data
  const translateCard = useCallback((card) => {
    // Map card names to translation keys
    const cardKeyMap = {
      // Major Arcana
      "0. The Fool": "tarot.fool",
      "1. The Magician": "tarot.magician",
      "2. The High Priestess": "tarot.highPriestess",
      "3. The Empress": "tarot.empress",
      "4. The Emperor": "tarot.emperor",
      "5. The Hierophant": "tarot.hierophant",
      "6. The Lovers": "tarot.lovers",
      "7. The Chariot": "tarot.chariot",
      "8. Strength": "tarot.strength",
      "9. The Hermit": "tarot.hermit",
      "10. Wheel of Fortune": "tarot.wheelOfFortune",
      "11. Justice": "tarot.justice",
      "12. The Hanged Man": "tarot.hangedMan",
      "13. Death": "tarot.death",
      "14. Temperance": "tarot.temperance",
      "15. The Devil": "tarot.devil",
      "16. The Tower": "tarot.tower",
      "17. The Star": "tarot.star",
      "18. The Moon": "tarot.moon",
      "19. The Sun": "tarot.sun",
      "20. Judgement": "tarot.judgement",
      "21. The World": "tarot.world",
      // Wands
      "Ace of Wands": "tarot.aceOfWands",
      "2 of Wands": "tarot.twoOfWands",
      "3 of Wands": "tarot.threeOfWands",
      "4 of Wands": "tarot.fourOfWands",
      "5 of Wands": "tarot.fiveOfWands",
      "6 of Wands": "tarot.sixOfWands",
      "7 of Wands": "tarot.sevenOfWands",
      "8 of Wands": "tarot.eightOfWands",
      "9 of Wands": "tarot.nineOfWands",
      "10 of Wands": "tarot.tenOfWands",
      "Page of Wands": "tarot.pageOfWands",
      "Knight of Wands": "tarot.knightOfWands",
      "Queen of Wands": "tarot.queenOfWands",
      "King of Wands": "tarot.kingOfWands",
      // Cups
      "Ace of Cups": "tarot.aceOfCups",
      "2 of Cups": "tarot.twoOfCups",
      "3 of Cups": "tarot.threeOfCups",
      "4 of Cups": "tarot.fourOfCups",
      "5 of Cups": "tarot.fiveOfCups",
      "6 of Cups": "tarot.sixOfCups",
      "7 of Cups": "tarot.sevenOfCups",
      "8 of Cups": "tarot.eightOfCups",
      "9 of Cups": "tarot.nineOfCups",
      "10 of Cups": "tarot.tenOfCups",
      "Page of Cups": "tarot.pageOfCups",
      "Knight of Cups": "tarot.knightOfCups",
      "Queen of Cups": "tarot.queenOfCups",
      "King of Cups": "tarot.kingOfCups",
      // Swords
      "Ace of Swords": "tarot.aceOfSwords",
      "2 of Swords": "tarot.twoOfSwords",
      "3 of Swords": "tarot.threeOfSwords",
      "4 of Swords": "tarot.fourOfSwords",
      "5 of Swords": "tarot.fiveOfSwords",
      "6 of Swords": "tarot.sixOfSwords",
      "7 of Swords": "tarot.sevenOfSwords",
      "8 of Swords": "tarot.eightOfSwords",
      "9 of Swords": "tarot.nineOfSwords",
      "10 of Swords": "tarot.tenOfSwords",
      "Page of Swords": "tarot.pageOfSwords",
      "Knight of Swords": "tarot.knightOfSwords",
      "Queen of Swords": "tarot.queenOfSwords",
      "King of Swords": "tarot.kingOfSwords",
      // Pentacles
      "Ace of Pentacles": "tarot.aceOfPentacles",
      "2 of Pentacles": "tarot.twoOfPentacles",
      "3 of Pentacles": "tarot.threeOfPentacles",
      "4 of Pentacles": "tarot.fourOfPentacles",
      "5 of Pentacles": "tarot.fiveOfPentacles",
      "6 of Pentacles": "tarot.sixOfPentacles",
      "7 of Pentacles": "tarot.sevenOfPentacles",
      "8 of Pentacles": "tarot.eightOfPentacles",
      "9 of Pentacles": "tarot.nineOfPentacles",
      "10 of Pentacles": "tarot.tenOfPentacles",
      "Page of Pentacles": "tarot.pageOfPentacles",
      "Knight of Pentacles": "tarot.knightOfPentacles",
      "Queen of Pentacles": "tarot.queenOfPentacles",
      "King of Pentacles": "tarot.kingOfPentacles"
    };

    // For reversed cards, remove "Reversed " prefix to get the base card key
    let cardKey = cardKeyMap[card.name];
    if (!cardKey && card.name.startsWith("Reversed ")) {
      const baseName = card.name.replace("Reversed ", "");
      cardKey = cardKeyMap[baseName];
    }

    // If we have a translation key, return translated card
    if (cardKey) {
      // Use reversedMeaning if the card position is reversed, otherwise use regular meaning
      const meaningKey = card.position === 'reversed' ? `${cardKey}.reversedMeaning` : `${cardKey}.meaning`;
      const translatedMeaning = t(meaningKey);
      
      return {
        ...card,
        name: t(`${cardKey}.name`),
        meaning: translatedMeaning || t(`${cardKey}.meaning`), // Fallback to regular meaning if reversed not available
        position: t(`tarot.position.${card.position}`),
        link: t(`${cardKey}.link`) || card.link // Use translated link or fall back to original
      };
    }

    // If no translation key found, return original card (for other suits)
    return {
      ...card,
      position: t(`tarot.position.${card.position}`)
    };
  }, [t]);
  
  // Get diary mutations for proper cache invalidation
  const { saveReading: saveReadingMutation, updateReading: updateReadingMutation } = useDiaryMutations();
  
  // Get spread configuration from navigation state (these might be overridden by restored state)
  const locationState = location.state || {};
  const { 
    spreadName: defaultSpreadName = t('spreadDrawing.customSpread'),
    cardCount: defaultCardCount = 1,
    spreadType: defaultSpreadType = "general",
    spreadInterpretations: defaultSpreadInterpretations = [],
    question = "",
    tarotCards = [],
    returnPath = "/",
    spreadConfig = {}
  } = locationState;

  // State for spread configuration (can be overridden by restored auth state)
  const [spreadName, setSpreadName] = useState(defaultSpreadName);
  const [cardCount, setCardCount] = useState(defaultCardCount);
  const [spreadType, setSpreadType] = useState(defaultSpreadType);
  const [spreadInterpretations, setSpreadInterpretations] = useState(defaultSpreadInterpretations);

  const [drawnCards, setDrawnCards] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [currentDrawIndex, setCurrentDrawIndex] = useState(0);
  const [notes, setNotes] = useState('');
  const [userQuestion, setUserQuestion] = useState(question);
  
  // AI-related states
  const [aiExplanation, setAiExplanation] = useState(null);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [aiError, setAiError] = useState(null);
  
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  // Card drawing states (from DailyCardPage)
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedCardIndex, setSelectedCardIndex] = useState(null);
  const [previewCardIndex, setPreviewCardIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartAngle, setDragStartAngle] = useState(0);
  const [cardRotationOffset, setCardRotationOffset] = useState(0);
  const [randomizedCards, setRandomizedCards] = useState([]);
  const [rotationInterval, setRotationInterval] = useState(null);
  const [isLongPressing, setIsLongPressing] = useState(false);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [drawnCardIndices, setDrawnCardIndices] = useState(new Set());
  const [drawnCardNames, setDrawnCardNames] = useState(new Set());

  // Scroll to top when results are shown
  useEffect(() => {
    if (showResults) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [showResults]);

  // Check for preserved state on initial page load (for auth returns)
  useEffect(() => {
    const pendingState = sessionStorage.getItem('pendingSpreadState');
    const redirectPath = sessionStorage.getItem('authRedirectPath');
    const currentPath = window.location.pathname + window.location.search;
    
    console.log('Initial page load check:', { 
      pendingState: !!pendingState, 
      redirectPath, 
      currentPath 
    });
    
    // If we have preserved state and we're on the expected path, restore everything
    if (pendingState && redirectPath && currentPath === redirectPath) {
      try {
        const savedState = JSON.parse(pendingState);
        console.log('Restoring complete saved spread state:', savedState);
        
        // Restore all the spread state
        if (savedState.drawnCards && savedState.drawnCards.length > 0) {
          setDrawnCards(savedState.drawnCards);
          setShowResults(true);
          setCurrentDrawIndex(savedState.drawnCards.length); // Set to completed state
          
          // Restore drawn card names to prevent duplicates
          if (savedState.drawnCardNames && Array.isArray(savedState.drawnCardNames)) {
            setDrawnCardNames(new Set(savedState.drawnCardNames));
          } else {
            // If not saved, reconstruct from drawn cards
            const cardNames = savedState.drawnCards.map(card => getBaseCardName(card.name));
            setDrawnCardNames(new Set(cardNames));
          }
          
          // Restore other state if available
          if (savedState.userQuestion !== undefined) setUserQuestion(savedState.userQuestion);
          if (savedState.notes !== undefined) setNotes(savedState.notes);
          if (savedState.aiExplanation !== undefined) setAiExplanation(savedState.aiExplanation);
          
          // IMPORTANT: Restore the spread configuration that was lost during auth redirect
          if (savedState.spreadName !== undefined) setSpreadName(savedState.spreadName);
          if (savedState.cardCount !== undefined) setCardCount(savedState.cardCount);
          if (savedState.spreadType !== undefined) setSpreadType(savedState.spreadType);
          if (savedState.spreadInterpretations !== undefined) setSpreadInterpretations(savedState.spreadInterpretations);
          
          console.log('Restored complete spread state from authentication return', {
            drawnCards: savedState.drawnCards.length,
            showResults: true,
            userQuestion: savedState.userQuestion,
            notes: savedState.notes,
            spreadName: savedState.spreadName,
            spreadInterpretations: savedState.spreadInterpretations?.length || 0
          });
        }
      } catch (error) {
        console.error('Error parsing saved spread state:', error);
      }
      
      // CRITICAL: Always clear sessionStorage after processing to prevent infinite loop
      sessionStorage.removeItem('pendingSpreadState');
      sessionStorage.removeItem('authRedirectPath');
      console.log('Cleared sessionStorage to prevent infinite loop');
    }
  }, []); // Run only on initial mount

  // Default tarot deck if none provided
  const defaultTarotCards = [
    // Major Arcana
{ name: "0. The Fool", position: "upright", meaning: "The Fool is the first card in the Major Arcana and represents the beginning of a new journey.", link: "https://tarotreadingcard.online/tarot-card-meanings/0-the-fool-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_0.jpg" },
            { name: "1. The Magician", position: "upright", meaning: "The Magician is a card of potential and manifestation, symbolizing the power to turn ideas into reality.", link: "https://tarotreadingcard.online/tarot-card-meanings/1-the-magician-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_1.jpg" },
            { name: "2. The High Priestess", position: "upright", meaning: "The High Priestess represents intuition, mystery, and the connection to the subconscious mind.", link: "https://tarotreadingcard.online/tarot-card-meanings/2-the-high-priestess-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_2.jpg" },
            { name: "3. The Empress", position: "upright", meaning: "The Empress is a card of abundance, nurturing energy, and creativity.", link: "https://tarotreadingcard.online/tarot-card-meanings/3-the-empress-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_3.jpg" },
            { name: "4. The Emperor", position: "upright", meaning: "The Emperor is a card of authority, structure, and stability.", link: "https://tarotreadingcard.online/tarot-card-meanings/4-the-emperor-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_4.jpg" },
            { name: "5. The Hierophant", position: "upright", meaning: "The Hierophant, also known as the High Priest, represents tradition, spirituality, and guidance.", link: "https://tarotreadingcard.online/tarot-card-meanings/5-the-hierophant-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_5.jpg" },
            { name: "6. The Lovers", position: "upright", meaning: "The Lovers card symbolizes union, harmony, and the power of choice.", link: "https://tarotreadingcard.online/tarot-card-meanings/6-the-lovers-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_6.jpg" },
            { name: "7. The Chariot", position: "upright", meaning: "The Chariot represents determination, willpower, and victory through focus and control.", link: "https://tarotreadingcard.online/tarot-card-meanings/7-the-chariot-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_7.jpg" },
            { name: "8. Strength", position: "upright", meaning: "The Strength card is a profound symbol of inner courage, self-mastery, and compassion.", link: "https://tarotreadingcard.online/tarot-card-meanings/8-strength-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_8.jpg" },
            { name: "9. The Hermit", position: "upright", meaning: "The Hermit is a card of introspection and inner guidance, symbolizing the journey inward to seek wisdom and understanding.", link: "https://tarotreadingcard.online/tarot-card-meanings/9-the-hermit-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_9.jpg" },
            { name: "10. Wheel of Fortune", position: "upright", meaning: "The Wheel of Fortune is a card of cycles, fate, and the ever-changing nature of life, symbolizing the ups and downs we all experience.", link: "https://tarotreadingcard.online/tarot-card-meanings/10-wheel-of-fortune-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_10.jpg" },
            { name: "11. Justice", position: "upright", meaning: "Justice is a card that represents fairness, truth, and the pursuit of balance.", link: "https://tarotreadingcard.online/tarot-card-meanings/11-justice-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_11.jpg" },
            { name: "12. The Hanged Man", position: "upright", meaning: "The Hanged Man is a card of surrender, sacrifice, and new perspectives, representing the transformative power of letting go and seeing things from a different angle.", link: "https://tarotreadingcard.online/tarot-card-meanings/12-the-hanged-man-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_12.jpg" },
            { name: "13. Death", position: "upright", meaning: "The Death card is a powerful symbol of transformation and rebirth, representing the cycles of life, endings, and new beginnings.", link: "https://tarotreadingcard.online/tarot-card-meanings/13-death-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_13.jpg" },
            { name: "14. Temperance", position: "upright", meaning: "Temperance is a card of balance, harmony, and moderation, symbolizing the art of blending opposites to achieve a greater whole.", link: "https://tarotreadingcard.online/tarot-card-meanings/14-temperance-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_14.jpg" },
            { name: "15. The Devil", position: "upright", meaning: "The Devil is a card that embodies temptation, bondage, and the darker aspects of human nature.", link: "https://tarotreadingcard.online/tarot-card-meanings/15-the-devil-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_15.jpg" },
            { name: "16. The Tower", position: "upright", meaning: "The Tower is a card of upheaval and revelation, symbolizing sudden change and the breaking down of old structures.", link: "https://tarotreadingcard.online/tarot-card-meanings/16-the-tower-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_16.jpg" },
            { name: "17. The Star", position: "upright", meaning: "The Star is a card of hope, inspiration, and serenity, symbolizing a sense of renewal and the promise of brighter days ahead.", link: "https://tarotreadingcard.online/tarot-card-meanings/17-the-star-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_17.jpg" },
            { name: "18. The Moon", position: "upright", meaning: "The Moon is a card of intuition, illusion, and the subconscious, representing the journey through the unknown and the exploration of hidden aspects of the self.", link: "https://tarotreadingcard.online/tarot-card-meanings/18-the-moon-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_18.jpg" },
            { name: "19. The Sun", position: "upright", meaning: "The Sun is a card of joy, success, and vitality, embodying the warmth and illumination that comes from positive energy and clarity.", link: "https://tarotreadingcard.online/tarot-card-meanings/19-the-sun-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_19.jpg" },
            { name: "20. Judgement", position: "upright", meaning: "Judgement is a card of renewal, rebirth, and awakening, symbolizing a profound transformation and a call to evaluate one's life choices.", link: "https://tarotreadingcard.online/tarot-card-meanings/20-judgement-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_20.jpg" },
            { name: "21. The World", position: "upright", meaning: "The World is a card of completion, fulfillment, and harmony, representing the culmination of a journey and the achievement of one's goals.", link: "https://tarotreadingcard.online/tarot-card-meanings/21-the-world-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_21.jpg" },

    // Wands (Fire/Action)
{ name: "Ace of Wands", position: "upright", meaning: "The Ace of Wands is a card of inspiration, new beginnings, and creative potential, symbolizing the spark of enthusiasm that ignites action and innovation.", link: "https://tarotreadingcard.online/tarot-card-meanings/ace-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_22.jpg" },
            { name: "2 of Wands", position: "upright", meaning: "The 2 of Wands is a card of exploration and future planning, symbolizing the moment of choice and the contemplation of one's path ahead.", link: "https://tarotreadingcard.online/tarot-card-meanings/2-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_23.jpg" },
            { name: "3 of Wands", position: "upright", meaning: "The 3 of Wands is a card that represents exploration, expansion, and foresight, symbolizing the anticipation of future opportunities and the potential for growth.", link: "https://tarotreadingcard.online/tarot-card-meanings/3-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_24.jpg" },
            { name: "4 of Wands", position: "upright", meaning: "The 4 of Wands is a card that celebrates harmony, celebration, and the joy of community.", link: "https://tarotreadingcard.online/tarot-card-meanings/4-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_25.jpg" },
            { name: "5 of Wands", position: "upright", meaning: "The 5 of Wands is a card that represents conflict, competition, and the challenges that arise when differing opinions clash.", link: "https://tarotreadingcard.online/tarot-card-meanings/5-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_26.jpg" },
            { name: "6 of Wands", position: "upright", meaning: "The 6 of Wands is a card of victory, recognition, and achievement, symbolizing the triumph that comes from hard work and perseverance.", link: "https://tarotreadingcard.online/tarot-card-meanings/6-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_27.jpg" },
            { name: "7 of Wands", position: "upright", meaning: "The 7 of Wands is a card of courage and perseverance, symbolizing the challenges that arise when standing one's ground and defending beliefs.", link: "https://tarotreadingcard.online/tarot-card-meanings/7-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_28.jpg" },
            { name: "8 of Wands", position: "upright", meaning: "The 8 of Wands is a card of swift movement and rapid progress, representing the energy of momentum and the swift unfolding of events.", link: "https://tarotreadingcard.online/tarot-card-meanings/8-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_29.jpg" },
            { name: "9 of Wands", position: "upright", meaning: "The 9 of Wands is a card of resilience and perseverance, representing the strength to endure through challenges and the determination to protect one’s hard-earned achievements.", link: "https://tarotreadingcard.online/tarot-card-meanings/9-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_30.jpg" },
            { name: "10 of Wands", position: "upright", meaning: "The 10 of Wands is a card that represents burden, responsibility, and the weight of obligations.", link: "https://tarotreadingcard.online/tarot-card-meanings/10-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_31.jpg" },
            { name: "Page of Wands", position: "upright", meaning: "The Page of Wands is a card of inspiration and enthusiasm, embodying the spirit of adventure and the pursuit of new ideas.", link: "https://tarotreadingcard.online/tarot-card-meanings/page-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_32.jpg" },
            { name: "Knight of Wands", position: "upright", meaning: "The Knight of Wands is a card of boldness, adventure, and dynamic energy, embodying the spirit of movement and exploration.", link: "https://tarotreadingcard.online/tarot-card-meanings/knight-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_33.jpg" },
            { name: "Queen of Wands", position: "upright", meaning: "The Queen of Wands is a card of warmth, intuition, and vibrant energy, symbolizing the embodiment of confidence and charisma.", link: "https://tarotreadingcard.online/tarot-card-meanings/queen-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_34.jpg" },
            { name: "King of Wands", position: "upright", meaning: "The King of Wands is a card of leadership, vision, and charisma, symbolizing the confident pursuit of goals and the ability to inspire others.", link: "https://tarotreadingcard.online/tarot-card-meanings/king-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_35.jpg" },

    // Cups (Hearts/Emotions)
{ name: "Ace of Cups", position: "upright", meaning: "The Ace of Cups is a card of emotional fulfillment and new beginnings in love and relationships, symbolizing the overflowing potential of the heart.", link: "https://tarotreadingcard.online/tarot-card-meanings/ace-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_36.jpg" },
            { name: "2 of Cups", position: "upright", meaning: "The 2 of Cups is a card that embodies the essence of love, partnership, and emotional connection.", link: "https://tarotreadingcard.online/tarot-card-meanings/2-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_37.jpg" },
            { name: "3 of Cups", position: "upright", meaning: "The 3 of Cups is a card that celebrates community, friendship, and joyous reunions.", link: "https://tarotreadingcard.online/tarot-card-meanings/3-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_38.jpg" },
            { name: "4 of Cups", position: "upright", meaning: "The 4 of Cups is a card that represents contemplation, reevaluation, and the need for introspection.", link: "https://tarotreadingcard.online/tarot-card-meanings/4-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_39.jpg" },
            { name: "5 of Cups", position: "upright", meaning: "The 5 of Cups is a card steeped in themes of loss, grief, and reflection, symbolizing the emotional turmoil that can follow disappointment.", link: "https://tarotreadingcard.online/tarot-card-meanings/5-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_40.jpg" },
            { name: "6 of Cups", position: "upright", meaning: "The 6 of Cups is a card that evokes nostalgia, innocence, and the joy of childhood memories, representing a return to simpler times and the blessings of past experiences.", link: "https://tarotreadingcard.online/tarot-card-meanings/6-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_41.jpg" },
            { name: "7 of Cups", position: "upright", meaning: "The 7 of Cups is a card of choices, dreams, and illusions, representing the myriad options and possibilities that life presents.", link: "https://tarotreadingcard.online/tarot-card-meanings/7-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_42.jpg" },
            { name: "8 of Cups", position: "upright", meaning: "The 8 of Cups is a card that represents the journey of emotional growth and the pursuit of deeper fulfillment.", link: "https://tarotreadingcard.online/tarot-card-meanings/8-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_43.jpg" },
            { name: "9 of Cups", position: "upright", meaning: "The 9 of Cups is often referred to as the wish card, representing emotional fulfillment, satisfaction, and the realization of desires.", link: "https://tarotreadingcard.online/tarot-card-meanings/9-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_44.jpg" },
            { name: "10 of Cups", position: "upright", meaning: "The 10 of Cups is a card that embodies emotional fulfillment, happiness, and harmony in relationships.", link: "https://tarotreadingcard.online/tarot-card-meanings/10-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_45.jpg" },
            { name: "Page of Cups", position: "upright", meaning: "The Page of Cups is a card of youthful creativity and emotional exploration, embodying the spirit of imagination and intuition.", link: "https://tarotreadingcard.online/tarot-card-meanings/page-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_46.jpg" },
            { name: "Knight of Cups", position: "upright", meaning: "The Knight of Cups is a card of romance, intuition, and emotional exploration, representing the quest for deeper connections and the pursuit of dreams.", link: "https://tarotreadingcard.online/tarot-card-meanings/knight-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_47.jpg" },
            { name: "Queen of Cups", position: "upright", meaning: "The Queen of Cups is a card of emotional depth, intuition, and nurturing energy, representing the compassionate and caring aspects of the feminine psyche.", link: "https://tarotreadingcard.online/tarot-card-meanings/queen-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_48.jpg" },
            { name: "King of Cups", position: "upright", meaning: "The King of Cups is a card of emotional balance, compassion, and wisdom, representing the mastery of the heart and the ability to navigate complex feelings with grace.", link: "https://tarotreadingcard.online/tarot-card-meanings/king-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_49.jpg" },
            
    // Swords (Air/Mind)
    { name: "Ace of Swords", position: "upright", meaning: "The Ace of Swords is a card of clarity, truth, and intellectual power, representing the dawn of new ideas and the potential for breakthrough moments.", link: "https://tarotreadingcard.online/tarot-card-meanings/ace-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_50.jpg" },
            { name: "2 of Swords", position: "upright", meaning: "The 2 of Swords represents a state of indecision and inner conflict, symbolizing the struggle between two opposing choices or viewpoints.", link: "https://tarotreadingcard.online/tarot-card-meanings/2-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_51.jpg" },
            { name: "3 of Swords", position: "upright", meaning: "The 3 of Swords is a card that embodies emotional pain, heartbreak, and the complexities of human relationships.", link: "https://tarotreadingcard.online/tarot-card-meanings/3-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_52.jpg" },
            { name: "4 of Swords", position: "upright", meaning: "The 4 of Swords is a card of rest, recovery, and contemplation, representing the need for a pause in the midst of life's challenges.", link: "https://tarotreadingcard.online/tarot-card-meanings/4-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_53.jpg" },
            { name: "5 of Swords", position: "upright", meaning: "The 5 of Swords is a card that embodies conflict, tension, and the complexities of human interactions.", link: "https://tarotreadingcard.online/tarot-card-meanings/5-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_54.jpg" },
            { name: "6 of Swords", position: "upright", meaning: "The 6 of Swords is a card that symbolizes transition, movement, and the journey towards healing and clarity.", link: "https://tarotreadingcard.online/tarot-card-meanings/6-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_55.jpg" },
            { name: "7 of Swords", position: "upright", meaning: "The 7 of Swords is a card that embodies strategy, cunning, and the art of deception.", link: "https://tarotreadingcard.online/tarot-card-meanings/7-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_56.jpg" },
            { name: "8 of Swords", position: "upright", meaning: "The 8 of Swords is a card that embodies feelings of restriction, confusion, and entrapment.", link: "https://tarotreadingcard.online/tarot-card-meanings/8-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_57.jpg" },
            { name: "9 of Swords", position: "upright", meaning: "The 9 of Swords is a card deeply rooted in anxiety, fear, and the weight of overwhelming thoughts.", link: "https://tarotreadingcard.online/tarot-card-meanings/9-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_58.jpg" },
            { name: "10 of Swords", position: "upright", meaning: "The 10 of Swords is a card that represents endings, betrayal, and the finality of a difficult situation.", link: "https://tarotreadingcard.online/tarot-card-meanings/10-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_59.jpg" },
            { name: "Page of Swords", position: "upright", meaning: "The Page of Swords is a card of curiosity, intellect, and youthful energy, symbolizing the pursuit of knowledge and truth.", link: "https://tarotreadingcard.online/tarot-card-meanings/page-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_60.jpg" },
            { name: "Knight of Swords", position: "upright", meaning: "The Knight of Swords is a card that embodies intellect, action, and the pursuit of truth, often representing a swift and decisive approach to challenges.", link: "https://tarotreadingcard.online/tarot-card-meanings/knight-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_61.jpg" },
            { name: "Queen of Swords", position: "upright", meaning: "The Queen of Swords is a card of intellect, clarity, and independence, representing the strength found in wisdom and the ability to discern truth from illusion.", link: "https://tarotreadingcard.online/tarot-card-meanings/queen-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_62.jpg" },
            { name: "King of Swords", position: "upright", meaning: "The King of Swords is a card of intellect, authority, and clear communication, embodying the qualities of a wise leader and strategist.", link: "https://tarotreadingcard.online/tarot-card-meanings/king-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_63.jpg" },
    
            // Pentacles (Earth/Material)

    { name: "Ace of Pentacles", position: "upright", meaning: "The Ace of Pentacles is a card of new beginnings and opportunities, representing the potential for wealth, prosperity, and material success.", link: "https://tarotreadingcard.online/tarot-card-meanings/ace-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_64.jpg" },
            { name: "2 of Pentacles", position: "upright", meaning: "The 2 of Pentacles represents balance, adaptability, and the juggling of multiple responsibilities.", link: "https://tarotreadingcard.online/tarot-card-meanings/2-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_65.jpg" },
            { name: "3 of Pentacles", position: "upright", meaning: "The 3 of Pentacles is a card that represents collaboration, skill, and the value of teamwork in the pursuit of common goals.", link: "https://tarotreadingcard.online/tarot-card-meanings/3-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_66.jpg" },
            { name: "4 of Pentacles", position: "upright", meaning: "The 4 of Pentacles is a card that represents stability, security, and the desire to hold on tightly to what one has.", link: "https://tarotreadingcard.online/tarot-card-meanings/4-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_67.jpg" },
            { name: "5 of Pentacles", position: "upright", meaning: "The 5 of Pentacles is a card that speaks to themes of hardship, struggle, and the experience of feeling left out in the cold.", link: "https://tarotreadingcard.online/tarot-card-meanings/5-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_68.jpg" },
            { name: "6 of Pentacles", position: "upright", meaning: "The 6 of Pentacles is a card that embodies the themes of generosity, balance, and the cyclical nature of giving and receiving.", link: "https://tarotreadingcard.online/tarot-card-meanings/6-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_69.jpg" },
            { name: "7 of Pentacles", position: "upright", meaning: "The 7 of Pentacles is a card that embodies patience, assessment, and the evaluation of progress.", link: "https://tarotreadingcard.online/tarot-card-meanings/7-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_70.jpg" },
            { name: "8 of Pentacles", position: "upright", meaning: "The 8 of Pentacles is a card that signifies dedication, craftsmanship, and the pursuit of mastery in one's work.", link: "https://tarotreadingcard.online/tarot-card-meanings/8-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_71.jpg" },
            { name: "9 of Pentacles", position: "upright", meaning: "The 9 of Pentacles is a card that embodies self-sufficiency, luxury, and the rewards of hard work.", link: "https://tarotreadingcard.online/tarot-card-meanings/9-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_72.jpg" },
            { name: "10 of Pentacles", position: "upright", meaning: "The 10 of Pentacles is a card that embodies legacy, stability, and the culmination of wealth and success.", link: "https://tarotreadingcard.online/tarot-card-meanings/10-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_73.jpg" },
            { name: "Page of Pentacles", position: "upright", meaning: "The Page of Pentacles is a card that embodies the spirit of ambition, diligence, and the pursuit of knowledge, particularly in matters of the material world.", link: "https://tarotreadingcard.online/tarot-card-meanings/page-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_74.jpg" },
            { name: "Knight of Pentacles", position: "upright", meaning: "The Knight of Pentacles is a card that embodies diligence, responsibility, and a strong work ethic.", link: "https://tarotreadingcard.online/tarot-card-meanings/knight-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_75.jpg" },
            { name: "Queen of Pentacles", position: "upright", meaning: "The Queen of Pentacles is a card of nurturing, practicality, and abundance, embodying the qualities of a grounded and resourceful individual.", link: "https://tarotreadingcard.online/tarot-card-meanings/queen-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_76.jpg" },
            { name: "King of Pentacles", position: "upright", meaning: "The King of Pentacles is a card that embodies stability, abundance, and material success, representing the culmination of hard work and a deep understanding of the physical world.", link: "https://tarotreadingcard.online/tarot-card-meanings/king-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_77.jpg" },    
    
    // Revised Meaning

                { name: "Reversed 0. The Fool", position: "reversed", meaning: "When The Fool appears reversed, it often signifies hesitation, recklessness, or a lack of preparation in the face of new beginnings.", link: "https://tarotreadingcard.online/tarot-card-meanings/0-the-fool-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_0.jpg" },
            { name: "Reversed 1. The Magician", position: "reversed", meaning: "When The Magician appears reversed, it often signifies blocked potential, misuse of power, or lack of focus.", link: "https://tarotreadingcard.online/tarot-card-meanings/1-the-magician-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_1.jpg" },
            { name: "Reversed 2. The High Priestess", position: "reversed", meaning: "When The High Priestess appears reversed, it signifies a disconnection from intuition, hidden truths being revealed, or difficulty accessing inner wisdom.", link: "https://tarotreadingcard.online/tarot-card-meanings/2-the-high-priestess-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_2.jpg" },
            { name: "Reversed 3. The Empress", position: "reversed", meaning: "When The Empress appears reversed, it signifies creative blocks, neglect, or an imbalance in nurturing energy.", link: "https://tarotreadingcard.online/tarot-card-meanings/3-the-empress-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_3.jpg" },
            { name: "Reversed 4. The Emperor", position: "reversed", meaning: "When The Emperor appears reversed, it signifies issues with authority, control, or structure.", link: "https://tarotreadingcard.online/tarot-card-meanings/4-the-emperor-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_4.jpg" },
            { name: "Reversed 5. The Hierophant", position: "reversed", meaning: "When The Hierophant appears reversed, it signifies rebellion, nonconformity, or a rejection of traditional values and systems.", link: "https://tarotreadingcard.online/tarot-card-meanings/5-the-hierophant-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_5.jpg" },
            { name: "Reversed 6. The Lovers", position: "reversed", meaning: "When The Lovers appears reversed, it signifies disharmony, misalignment, or conflict in relationships or within yourself.", link: "https://tarotreadingcard.online/tarot-card-meanings/6-the-lovers-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_6.jpg" },
            { name: "Reversed 7. The Chariot", position: "reversed", meaning: "When The Chariot appears reversed, it signifies a lack of control, direction, or focus.", link: "https://tarotreadingcard.online/tarot-card-meanings/7-the-chariot-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_7.jpg" },
            { name: "Reversed 8. Strength", position: "reversed", meaning: "In the realm of reversed meanings, the concept of strength takes on a contrasting hue.", link: "https://tarotreadingcard.online/tarot-card-meanings/8-strength-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_8.jpg" },
            { name: "Reversed 9. The Hermit", position: "reversed", meaning: "The Hermit, when reversed, brings forth a sense of confusion and disconnection.", link: "https://tarotreadingcard.online/tarot-card-meanings/9-the-hermit-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_9.jpg" },
            { name: "Reversed 10. Wheel of Fortune", position: "reversed", meaning: "In the context of the reversed Wheel of Fortune, one might feel stuck in a cycle of misfortune and unpredictability.", link: "https://tarotreadingcard.online/tarot-card-meanings/10-wheel-of-fortune-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_10.jpg" },
            { name: "Reversed 11. Justice", position: "reversed", meaning: "The concept of justice often embodies fairness, equality, and the pursuit of truth.", link: "https://tarotreadingcard.online/tarot-card-meanings/11-justice-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_11.jpg" },
            { name: "Reversed 12. The Hanged Man", position: "reversed", meaning: "In traditional tarot, The Hanged Man represents a state of suspension, surrender, and introspection.", link: "https://tarotreadingcard.online/tarot-card-meanings/12-the-hanged-man-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_12.jpg" },
            { name: "Reversed 13. Death", position: "reversed", meaning: "In the realm of symbolism, the card representing Death often conveys a sense of finality and transformation.", link: "https://tarotreadingcard.online/tarot-card-meanings/13-death-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_13.jpg" },
            { name: "Reversed 14. Temperance", position: "reversed", meaning: "Temperance, often associated with moderation and balance, takes on a starkly different meaning when viewed through the lens of excess and indulgence.", link: "https://tarotreadingcard.online/tarot-card-meanings/14-temperance-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_14.jpg" },
            { name: "Reversed 15. The Devil", position: "reversed", meaning: "In the context of tarot, The Devil card traditionally signifies temptation, materialism, and bondage to negative influences.", link: "https://tarotreadingcard.online/tarot-card-meanings/15-the-devil-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_15.jpg" },
            { name: "Reversed 16. The Tower", position: "reversed", meaning: "The Tower card in reversed meaning signifies a period of avoidance and resistance to change.", link: "https://tarotreadingcard.online/tarot-card-meanings/16-the-tower-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_16.jpg" },
            { name: "Reversed 17. The Star", position: "reversed", meaning: "The Star, in its reversed position, reflects a sense of disillusionment and loss of hope.", link: "https://tarotreadingcard.online/tarot-card-meanings/17-the-star-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_17.jpg" },
            { name: "Reversed 18. The Moon", position: "reversed", meaning: "The Moon, when reversed, presents a contrasting landscape filled with clarity and resolution.", link: "https://tarotreadingcard.online/tarot-card-meanings/18-the-moon-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_18.jpg" },
            { name: "Reversed 19. The Sun", position: "reversed", meaning: "The Sun reversed suggests a period of darkness and confusion, where clarity and joy are obscured.", link: "https://tarotreadingcard.online/tarot-card-meanings/19-the-sun-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_19.jpg" },
            { name: "Reversed 20. Judgement", position: "reversed", meaning: "In the realm of reversed meanings, the concept of Judgement takes on a significantly different hue.", link: "https://tarotreadingcard.online/tarot-card-meanings/20-judgement-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_20.jpg" },
            { name: "Reversed 21. The World", position: "reversed", meaning: "In a world where possibilities seem endless, The World tarot card symbolizes completion and fulfillment.", link: "https://tarotreadingcard.online/tarot-card-meanings/21-the-world-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_21.jpg" },
            { name: "Reversed Ace of Wands", position: "reversed", meaning: "The Ace of Wands typically signifies new beginnings, inspiration, and a surge of creative energy.", link: "https://tarotreadingcard.online/tarot-card-meanings/ace-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_22.jpg" },
            { name: "Reversed 2 of Wands", position: "reversed", meaning: "The 2 of Wands typically signifies planning, foresight, and the potential for new ventures.", link: "https://tarotreadingcard.online/tarot-card-meanings/2-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_23.jpg" },
            { name: "Reversed 3 of Wands", position: "reversed", meaning: "The 3 of Wands, when interpreted in reverse, often signifies a sense of stagnation and missed opportunities.", link: "https://tarotreadingcard.online/tarot-card-meanings/3-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_24.jpg" },
            { name: "Reversed 4 of Wands", position: "reversed", meaning: "The 4 of Wands typically signifies celebration, harmony, and a sense of belonging.", link: "https://tarotreadingcard.online/tarot-card-meanings/4-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_25.jpg" },
            { name: "Reversed 5 of Wands", position: "reversed", meaning: "The 5 of Wands typically represents conflict, competition, and struggle among a group.", link: "https://tarotreadingcard.online/tarot-card-meanings/5-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_26.jpg" },
            { name: "Reversed 6 of Wands", position: "reversed", meaning: "The 6 of Wands typically represents victory, recognition, and achievement.", link: "https://tarotreadingcard.online/tarot-card-meanings/6-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_27.jpg" },
            { name: "Reversed 7 of Wands", position: "reversed", meaning: "The 7 of Wands typically signifies a stance of defense and resilience in the face of challenges.", link: "https://tarotreadingcard.online/tarot-card-meanings/7-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_28.jpg" },
            { name: "Reversed 8 of Wands", position: "reversed", meaning: "The 8 of Wands typically signifies swift movement, progress, and rapid developments.", link: "https://tarotreadingcard.online/tarot-card-meanings/8-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_29.jpg" },
            { name: "Reversed 9 of Wands", position: "reversed", meaning: "The 9 of Wands typically symbolizes resilience and determination in the face of adversity.", link: "https://tarotreadingcard.online/tarot-card-meanings/9-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_30.jpg" },
            { name: "Reversed 10 of Wands", position: "reversed", meaning: "The 10 of Wands typically represents burdens, responsibilities, and the feeling of being overwhelmed.", link: "https://tarotreadingcard.online/tarot-card-meanings/10-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_31.jpg" },
            { name: "Reversed Page of Wands", position: "reversed", meaning: "The Page of Wands reversed often signifies a lack of direction and purpose.", link: "https://tarotreadingcard.online/tarot-card-meanings/page-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_32.jpg" },
            { name: "Reversed Knight of Wands", position: "reversed", meaning: "The Knight of Wands, when reversed, embodies a sense of recklessness and lack of direction.", link: "https://tarotreadingcard.online/tarot-card-meanings/knight-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_33.jpg" },
            { name: "Reversed Queen of Wands", position: "reversed", meaning: "In the reversed position, the Queen of Wands embodies a sense of insecurity and self-doubt.", link: "https://tarotreadingcard.online/tarot-card-meanings/queen-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_34.jpg" },
            { name: "Reversed King of Wands", position: "reversed", meaning: "The King of Wands, when reversed, embodies a sense of chaos and disorganization.", link: "https://tarotreadingcard.online/tarot-card-meanings/king-of-wands-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_35.jpg" },
            { name: "Reversed Ace of Cups", position: "reversed", meaning: "The Ace of Cups, when reversed, signifies a disruption in emotional harmony and the potential for inner turmoil.", link: "https://tarotreadingcard.online/tarot-card-meanings/ace-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_36.jpg" },
            { name: "Reversed 2 of Cups", position: "reversed", meaning: "The 2 of Cups traditionally signifies a deep bond or connection between two individuals, often highlighting love, partnership, and mutual respect.", link: "https://tarotreadingcard.online/tarot-card-meanings/2-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_37.jpg" },
            { name: "Reversed 3 of Cups", position: "reversed", meaning: "The 3 of Cups, when viewed in reverse, signifies a disconnection from the sense of community and celebration that typically characterizes this card.", link: "https://tarotreadingcard.online/tarot-card-meanings/3-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_38.jpg" },
            { name: "Reversed 4 of Cups", position: "reversed", meaning: "The 4 of Cups typically represents a sense of apathy or contemplation, where one may feel disconnected from the opportunities and experiences around them.", link: "https://tarotreadingcard.online/tarot-card-meanings/4-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_39.jpg" },
            { name: "Reversed 5 of Cups", position: "reversed", meaning: "The 5 of Cups typically symbolizes loss, disappointment, and mourning over what has been lost.", link: "https://tarotreadingcard.online/tarot-card-meanings/5-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_40.jpg" },
            { name: "Reversed 6 of Cups", position: "reversed", meaning: "The 6 of Cups reversed often represents a disconnection from the past, indicating that nostalgia may be overshadowed by unresolved issues.", link: "https://tarotreadingcard.online/tarot-card-meanings/6-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_41.jpg" },
            { name: "Reversed 7 of Cups", position: "reversed", meaning: "The 7 of Cups, when viewed in its reversed position, signifies a sense of clarity and decisiveness that often feels elusive in its upright state.", link: "https://tarotreadingcard.online/tarot-card-meanings/7-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_42.jpg" },
            { name: "Reversed 8 of Cups", position: "reversed", meaning: "The 8 of Cups, when reversed, speaks to a state of stagnation and an inability to move on from situations that are no longer serving you.", link: "https://tarotreadingcard.online/tarot-card-meanings/8-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_43.jpg" },
            { name: "Reversed 9 of Cups", position: "reversed", meaning: "The 9 of Cups, often referred to as the WISH CARD typically signifies emotional fulfillment and contentment.", link: "https://tarotreadingcard.online/tarot-card-meanings/9-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_44.jpg" },
            { name: "Reversed 10 of Cups", position: "reversed", meaning: "The 10 of Cups, when reversed, often signifies disconnection and dissatisfaction within personal relationships.", link: "https://tarotreadingcard.online/tarot-card-meanings/10-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_45.jpg" },
            { name: "Reversed Page of Cups", position: "reversed", meaning: "The Page of Cups, often associated with youthful creativity and emotional exploration, can take on a starkly different interpretation when viewed through a reversed lens.", link: "https://tarotreadingcard.online/tarot-card-meanings/page-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_46.jpg" },
            { name: "Reversed Knight of Cups", position: "reversed", meaning: "The Knight of Cups, traditionally a symbol of romance and idealism, takes on a contrasting meaning when reversed.", link: "https://tarotreadingcard.online/tarot-card-meanings/knight-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_47.jpg" },
            { name: "Reversed Queen of Cups", position: "reversed", meaning: "The Queen of Cups, when reversed, often embodies emotional instability and an overwhelming sense of insecurity.", link: "https://tarotreadingcard.online/tarot-card-meanings/queen-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_48.jpg" },
            { name: "Reversed King of Cups", position: "reversed", meaning: "The King of Cups, when reversed, embodies emotional instability and a lack of control over one's feelings.", link: "https://tarotreadingcard.online/tarot-card-meanings/king-of-cups-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_49.jpg" },
            { name: "Reversed Ace of Swords", position: "reversed", meaning: "The Ace of Swords, when reversed, embodies a sense of confusion and mental turmoil.", link: "https://tarotreadingcard.online/tarot-card-meanings/ace-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_50.jpg" },
            { name: "Reversed 2 of Swords", position: "reversed", meaning: "The 2 of Swords often embodies conflict, indecision, and the inability to see a clear path forward.", link: "https://tarotreadingcard.online/tarot-card-meanings/2-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_51.jpg" },
            { name: "Reversed 3 of Swords", position: "reversed", meaning: "The 3 of Swords, often associated with heartbreak and emotional pain, finds its reversed meaning rooted in healing and the release of past wounds.", link: "https://tarotreadingcard.online/tarot-card-meanings/3-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_52.jpg" },
            { name: "Reversed 4 of Swords", position: "reversed", meaning: "The 4 of Swords typically embodies rest, recovery, and a time for contemplation.", link: "https://tarotreadingcard.online/tarot-card-meanings/4-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_53.jpg" },
            { name: "Reversed 5 of Swords", position: "reversed", meaning: "The 5 of Swords typically signifies conflict and tension, often highlighting themes of betrayal, defeat, or hollow victories.", link: "https://tarotreadingcard.online/tarot-card-meanings/5-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_54.jpg" },
            { name: "Reversed 6 of Swords", position: "reversed", meaning: "The 6 of Swords typically represents a journey away from difficulties towards a place of calm and healing.", link: "https://tarotreadingcard.online/tarot-card-meanings/6-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_55.jpg" },
            { name: "Reversed 7 of Swords", position: "reversed", meaning: "The 7 of Swords typically embodies themes of stealth, cunning, and deception.", link: "https://tarotreadingcard.online/tarot-card-meanings/7-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_56.jpg" },
            { name: "Reversed 8 of Swords", position: "reversed", meaning: "The 8 of Swords typically depicts a figure confined and surrounded by a barrier of swords, symbolizing feelings of restriction, confusion, and helplessness.", link: "https://tarotreadingcard.online/tarot-card-meanings/8-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_57.jpg" },
            { name: "Reversed 9 of Swords", position: "reversed", meaning: "The 9 of Swords traditionally represents anxiety, nightmares, and feelings of despair.", link: "https://tarotreadingcard.online/tarot-card-meanings/9-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_58.jpg" },
            { name: "Reversed 10 of Swords", position: "reversed", meaning: "The 10 of Swords typically represents a situation of utter defeat and betrayal, yet when we consider its reversed meaning, we find a glimmer of hope emerging from the depths of despair.", link: "https://tarotreadingcard.online/tarot-card-meanings/10-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_59.jpg" },
            { name: "Reversed Page of Swords", position: "reversed", meaning: "The Page of Swords typically represents curiosity, vigilance, and the pursuit of knowledge.", link: "https://tarotreadingcard.online/tarot-card-meanings/page-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_60.jpg" },
            { name: "Reversed Knight of Swords", position: "reversed", meaning: "The Knight of Swords typically embodies swift action, decisiveness, and a bold approach to challenges.", link: "https://tarotreadingcard.online/tarot-card-meanings/knight-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_61.jpg" },
            { name: "Reversed Queen of Swords", position: "reversed", meaning: "The Queen of Swords typically embodies clarity, intellect, and honesty.", link: "https://tarotreadingcard.online/tarot-card-meanings/queen-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_62.jpg" },
            { name: "Reversed King of Swords", position: "reversed", meaning: "The King of Swords, when reversed, embodies confusion and a lack of clarity in thought.", link: "https://tarotreadingcard.online/tarot-card-meanings/king-of-swords-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_63.jpg" },
            { name: "Reversed Ace of Pentacles", position: "reversed", meaning: "The Ace of Pentacles, typically representing new beginnings in the material realm, often symbolizes prosperity, financial opportunities, and the promise of security.", link: "https://tarotreadingcard.online/tarot-card-meanings/ace-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_64.jpg" },
            { name: "Reversed 2 of Pentacles", position: "reversed", meaning: "The 2 of Pentacles, when interpreted in its reversed state, signifies a struggle with balance and prioritization.", link: "https://tarotreadingcard.online/tarot-card-meanings/2-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_65.jpg" },
            { name: "Reversed 3 of Pentacles", position: "reversed", meaning: "The Three of Pentacles in reverse often signifies a breakdown in collaboration and teamwork.", link: "https://tarotreadingcard.online/tarot-card-meanings/3-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_66.jpg" },
            { name: "Reversed 4 of Pentacles", position: "reversed", meaning: "The 4 of Pentacles typically depicts a figure tightly clutching their possessions, symbolizing a strong attachment to material wealth and a fear of loss.", link: "https://tarotreadingcard.online/tarot-card-meanings/4-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_67.jpg" },
            { name: "Reversed 5 of Pentacles", position: "reversed", meaning: "The 5 of Pentacles typically symbolizes hardship, financial struggle, and a sense of isolation.", link: "https://tarotreadingcard.online/tarot-card-meanings/5-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_68.jpg" },
            { name: "Reversed 6 of Pentacles", position: "reversed", meaning: "The 6 of Pentacles reversed often highlights a sense of imbalance and inequity in the realms of giving and receiving.", link: "https://tarotreadingcard.online/tarot-card-meanings/6-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_69.jpg" },
            { name: "Reversed 7 of Pentacles", position: "reversed", meaning: "The Reversed 7 of Pentacles often signifies a sense of impatience and frustration with progress.", link: "https://tarotreadingcard.online/tarot-card-meanings/7-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_70.jpg" },
            { name: "Reversed 8 of Pentacles", position: "reversed", meaning: "The 8 of Pentacles typically represents hard work, dedication, and the pursuit of mastery in a particular skill or trade.", link: "https://tarotreadingcard.online/tarot-card-meanings/8-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_71.jpg" },
            { name: "Reversed 9 of Pentacles", position: "reversed", meaning: "The 9 of Pentacles, when reversed, often signifies a sense of disconnection from the rewards that typically accompany hard work and self-sufficiency.", link: "https://tarotreadingcard.online/tarot-card-meanings/9-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_72.jpg" },
            { name: "Reversed 10 of Pentacles", position: "reversed", meaning: "The 10 of Pentacles, when reversed, speaks to a disconnection from the traditional values of family, heritage, and long-term stability.", link: "https://tarotreadingcard.online/tarot-card-meanings/10-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_73.jpg" },
            { name: "Reversed Page of Pentacles", position: "reversed", meaning: "The Page of Pentacles reversed often embodies a sense of stagnation and missed opportunities.", link: "https://tarotreadingcard.online/tarot-card-meanings/page-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_74.jpg" },
            { name: "Reversed Knight of Pentacles", position: "reversed", meaning: "The Knight of Pentacles often embodies diligence, responsibility, and a methodical approach to life.", link: "https://tarotreadingcard.online/tarot-card-meanings/knight-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_75.jpg" },
            { name: "Reversed Queen of Pentacles", position: "reversed", meaning: "The Queen of Pentacles in reverse often embodies a sense of neglect regarding practical matters and responsibilities.", link: "https://tarotreadingcard.online/tarot-card-meanings/queen-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_76.jpg" },
            { name: "Reversed King of Pentacles", position: "reversed", meaning: "The King of Pentacles reversed often embodies a sense of instability and lack of direction in the material world.", link: "https://tarotreadingcard.online/tarot-card-meanings/king-of-pentacles-tarot-card-meanings-free-tarot-reading/", image: "https://tarotreadingcard.online/wp-content/uploads/2024/12/image_77.jpg" }
  ];

  const cardsToUse = tarotCards.length > 0 ? tarotCards : defaultTarotCards;

  // Helper function to get correct image index for both upright and reversed cards
  const getImageIndex = (card) => {
    if (card.originalIndex !== undefined) {
      // If originalIndex is within the first 78 cards (upright versions), use it directly
      if (card.originalIndex < 78) {
        return card.originalIndex;
      }
      // If originalIndex is 78 or higher (reversed versions), map it back to upright version
      return card.originalIndex - 78;
    }
    
    // Fallback: try to extract number from card name
    const match = card.name.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  // Initialize randomized cards on component mount
  React.useEffect(() => {
    const shuffleCards = () => {
      // Only use the first 78 cards (upright versions) to avoid duplicates in circle
      const uniqueCardIndices = Array.from({ length: 78 }, (_, i) => i);
      
      for (let i = uniqueCardIndices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [uniqueCardIndices[i], uniqueCardIndices[j]] = [uniqueCardIndices[j], uniqueCardIndices[i]];
      }
      
      return uniqueCardIndices.map((originalIndex, circlePosition) => ({
        originalIndex,
        circlePosition
      }));
    };
    
    setRandomizedCards(shuffleCards());
    setHasInitiallyLoaded(true);
  }, [cardsToUse.length]);

  // Helper function to get base card name (removes "Reversed " prefix)
  const getBaseCardName = useCallback((cardName) => {
    return cardName.replace(/^Reversed /, '');
  }, []);

  // Card selection logic (adapted from DailyCardPage for multiple cards)
  const selectCard = (circleIndex, e) => {
    if (!isDrawing && randomizedCards.length > 0 && circleIndex < randomizedCards.length && !drawnCardIndices.has(circleIndex)) {
      e.stopPropagation();
      
      const randomizedCard = randomizedCards[circleIndex];
      if (!randomizedCard || !cardsToUse[randomizedCard.originalIndex]) {
        console.warn('Invalid card data for index:', circleIndex);
        return;
      }

      const selectedCardData = cardsToUse[randomizedCard.originalIndex];
      const baseCardName = getBaseCardName(selectedCardData.name);
      
      // Check if this base card has already been drawn (prevents same card upright + reversed)
      if (drawnCardNames.has(baseCardName)) {
        console.log('Card already drawn:', baseCardName);
        return; // Prevent drawing the same card twice
      }
      
      if (previewCardIndex === circleIndex) {
        // Second click - select and draw the card
        setSelectedCardIndex(circleIndex);
        setPreviewCardIndex(null);
        setIsDrawing(true);
        
        // Randomly decide if this card should be reversed (50% chance)
        const isReversed = Math.random() < 0.5;
        let finalCard = selectedCardData;
        let finalOriginalIndex = randomizedCard.originalIndex;
        
        if (isReversed && randomizedCard.originalIndex < 78) {
          // Use reversed version (index + 78)
          const reversedIndex = randomizedCard.originalIndex + 78;
          if (reversedIndex < cardsToUse.length) {
            finalCard = cardsToUse[reversedIndex];
            finalOriginalIndex = reversedIndex;
          }
        }
        
        const selectedCard = {
          ...finalCard,
          originalIndex: finalOriginalIndex
        };
        
        const newDrawnCards = [...drawnCards, selectedCard];
        setDrawnCards(newDrawnCards);
        
        // Mark this card as drawn by both circle index and base card name
        setDrawnCardIndices(prev => new Set([...prev, circleIndex]));
        setDrawnCardNames(prev => new Set([...prev, baseCardName]));
        
        // Check if we've drawn all cards
        if (newDrawnCards.length >= cardCount) {
          setShowResults(true);
          
          // Track spread_drawn event with spread type in event name
          const spreadTypeFormatted = (spreadType || 'general').toLowerCase().replace(/\s+/g, '_');
          analytics.trackSpreadDrawn(spreadTypeFormatted, cardCount);
        } else {
          // Reset selection states for next card but keep the same circle
          setCurrentDrawIndex(newDrawnCards.length);
          setSelectedCardIndex(null);
          setPreviewCardIndex(null);
        }
        
        setIsDrawing(false);
      } else {
        // First click - preview the card
        setPreviewCardIndex(circleIndex);
        setSelectedCardIndex(null);
      }
    }
  };

  // Event handling functions (from DailyCardPage)
  const getEventPosition = (e) => {
    if (e.touches && e.touches.length > 0) {
      return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
    } else if (e.changedTouches && e.changedTouches.length > 0) {
      return { clientX: e.changedTouches[0].clientX, clientY: e.changedTouches[0].clientY };
    }
    return { clientX: e.clientX, clientY: e.clientY };
  };

  const handleStart = (e) => {
    if (isDrawing) return;
    
    e.preventDefault();
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const pos = getEventPosition(e);
    const startAngle = Math.atan2(pos.clientY - centerY, pos.clientX - centerX);
    setDragStartAngle(startAngle);
  };

  const handleMove = useCallback((e) => {
    if (!isDragging || isDrawing) return;
    
    e.preventDefault();
    
    const circle = document.querySelector('.card-circle');
    if (!circle) return;
    
    const rect = circle.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const pos = getEventPosition(e);
    const currentAngle = Math.atan2(pos.clientY - centerY, pos.clientX - centerX);
    const angleDiff = currentAngle - dragStartAngle;
    
    setCardRotationOffset(prev => prev + (angleDiff * 180 / Math.PI));
    setDragStartAngle(currentAngle);
  }, [isDragging, isDrawing, dragStartAngle]);

  const handleEnd = useCallback((e) => {
    setIsDragging(false);
  }, []);

  // Global event listeners
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMove, { passive: false });
      document.addEventListener('mouseup', handleEnd, { passive: false });
      document.addEventListener('touchmove', handleMove, { passive: false });
      document.addEventListener('touchend', handleEnd, { passive: false });
      document.addEventListener('touchcancel', handleEnd, { passive: false });
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
      document.removeEventListener('touchcancel', handleEnd);
    };
  }, [isDragging, handleMove, handleEnd]);

  // Rotation control functions
  const startRotation = (direction) => {
    const rotationAmount = 15;
    const rotationSpeed = 100;
    
    if (direction === 'left') {
      setCardRotationOffset(prev => prev - rotationAmount);
    } else {
      setCardRotationOffset(prev => prev + rotationAmount);
    }
    
    if (rotationInterval) {
      clearInterval(rotationInterval);
    }
    
    const startTime = Date.now();
    const delayBeforeContinuous = 300;
    
    const interval = setInterval(() => {
      if (Date.now() - startTime > delayBeforeContinuous) {
        if (direction === 'left') {
          setCardRotationOffset(prev => prev - rotationAmount);
        } else {
          setCardRotationOffset(prev => prev + rotationAmount);
        }
      }
    }, rotationSpeed);
    
    setRotationInterval(interval);
    setIsLongPressing(true);
  };
  
  const stopRotation = () => {
    if (rotationInterval) {
      clearInterval(rotationInterval);
      setRotationInterval(null);
    }
    setIsLongPressing(false);
  };

  // Button event handlers
  const handleLeftMouseDown = () => startRotation('left');
  const handleLeftMouseUp = () => stopRotation();
  const handleLeftTouchStart = (e) => {
    e.preventDefault();
    startRotation('left');
  };
  const handleLeftTouchEnd = (e) => {
    e.preventDefault();
    stopRotation();
  };
  
  const handleRightMouseDown = () => startRotation('right');
  const handleRightMouseUp = () => stopRotation();
  const handleRightTouchStart = (e) => {
    e.preventDefault();
    startRotation('right');
  };
  const handleRightTouchEnd = (e) => {
    e.preventDefault();
    stopRotation();
  };

  // Cleanup
  React.useEffect(() => {
    return () => {
      setIsDragging(false);
      if (rotationInterval) {
        clearInterval(rotationInterval);
      }
    };
  }, [rotationInterval]);

  // Function to draw all cards automatically
  const drawAllCards = () => {
    if (isDrawing || showResults) return;
    
    setIsDrawing(true);
    
    // Create a shuffled copy of all 78 tarot cards (indices 0-77)
    const allCardIndices = Array.from({ length: 78 }, (_, i) => i);
    
    // Shuffle the indices
    for (let i = allCardIndices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allCardIndices[i], allCardIndices[j]] = [allCardIndices[j], allCardIndices[i]];
    }
    
    // Take the first cardCount cards from the shuffled array
    const selectedIndices = allCardIndices.slice(0, cardCount);
    
    // Create drawn cards array with random orientations
    const newDrawnCards = selectedIndices.map(originalIndex => {
      const cardData = cardsToUse[originalIndex];
      
      // Randomly determine if card should be reversed (50% chance)
      const isReversed = Math.random() < 0.5;
      
      if (isReversed && originalIndex < 78) {
        // If we want reversed and it's an upright card, use the reversed version
        const reversedIndex = originalIndex + 78;
        if (reversedIndex < cardsToUse.length) {
          return {
            ...cardsToUse[reversedIndex],
            originalIndex: reversedIndex
          };
        }
      }
      
      // Return upright version
      return {
        ...cardData,
        originalIndex: originalIndex
      };
    });
    
    // Update state with all drawn cards
    setDrawnCards(newDrawnCards);
    setCurrentDrawIndex(cardCount);
    
    // Update drawn card names to prevent duplicates in card circle
    const drawnNames = newDrawnCards.map(card => getBaseCardName(card.name));
    setDrawnCardNames(new Set(drawnNames));
    
    // Clear the card circle indices since we're showing results
    setDrawnCardIndices(new Set());
    
    setShowResults(true);
    setIsDrawing(false);
    
    // Track analytics event
    const spreadTypeFormatted = (spreadType || 'general').toLowerCase().replace(/\s+/g, '_');
    analytics.trackSpreadDrawn(spreadTypeFormatted, cardCount);
  };

  const resetSpread = () => {
    setDrawnCards([]);
    setShowResults(false);
    setCurrentDrawIndex(0);
    setNotes('');
    setSelectedCardIndex(null);
    setPreviewCardIndex(null);
    setHasInitiallyLoaded(false);
    setDrawnCardIndices(new Set());
    setDrawnCardNames(new Set());
    
    // Re-initialize the full card circle for a new spread
    const shuffleCards = () => {
      // Only use the first 78 cards (upright versions) to avoid duplicates in circle
      const uniqueCardIndices = Array.from({ length: 78 }, (_, i) => i);
      
      for (let i = uniqueCardIndices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [uniqueCardIndices[i], uniqueCardIndices[j]] = [uniqueCardIndices[j], uniqueCardIndices[i]];
      }
      
      return uniqueCardIndices.map((originalIndex, circlePosition) => ({
        originalIndex,
        circlePosition
      }));
    };
    
    setRandomizedCards(shuffleCards());
    setTimeout(() => setHasInitiallyLoaded(true), 100);
  };

  const getCurrentPosition = () => {
    return spreadInterpretations[currentDrawIndex] || `${t('spreadDrawing.cardPosition')} ${currentDrawIndex + 1}`;
  };

  const saveReading = async () => {
    try {
      // Transform cards to match the expected structure in DiaryPage
      const transformedCards = drawnCards.map((card, index) => {
        const translatedCard = translateCard(card);
        return {
          position: spreadInterpretations[index] || `${t('spreadDrawing.cardPosition')} ${index + 1}`,
          card: {
            name: translatedCard.name,
            position: translatedCard.position, // This is the upright/reversed status
            meaning: translatedCard.meaning,
            image: card.image,
            link: translatedCard.link
          }
        };
      });

      // Don't save payment success messages as AI explanations
      const isPaymentSuccessMessage = aiExplanation && aiExplanation.includes('Payment Successful');
      const aiExplanationToSave = isPaymentSuccessMessage ? null : aiExplanation;

      const readingData = {
        spread: spreadName,
        spreadName: spreadName, // Add this field for compatibility
        spreadType: spreadType, // Add this field for compatibility
        cards: transformedCards,
        question: userQuestion,
        notes: notes,
        aiExplanation: aiExplanationToSave, // Only save real AI explanations, not payment messages
        timestamp: new Date().toISOString(), // Ensure we include timestamp at top level
        readingDate: new Date().toISOString(), // Alternative timestamp field
        metadata: {
          type: 'spread_reading',
          spreadType,
          cardCount,
          spreadConfig,
          date: new Date().toISOString(),
          hasAIExplanation: !!aiExplanationToSave
        }
      };
      
      console.log('=== DEBUGGING SPREAD NAME ISSUE ===');
      console.log('spreadName from props:', spreadName);
      console.log('location.state:', location.state);
      console.log('Original drawnCards:', drawnCards);
      console.log('Transformed cards:', transformedCards);
      console.log('Saving spread reading:', readingData);
      console.log('===============================');
      
      // Use mutation for proper cache invalidation
      const result = await saveReadingMutation.mutateAsync(readingData);
      console.log('Reading saved successfully with result:', result);
      
      // Small delay to ensure cache updates complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Navigate to diary after successful save
      navigate('/diary');
    } catch (error) {
      console.error('Error saving reading:', error);
      alert(t('spreadDrawing.failedToSave'));
    }
  };

  const generateAIExplanation = async () => {
    try {
      setIsGeneratingAI(true);
      setAiError(null);
      
      // Format the spread data for AI processing
      const spreadData = aiService.formatSpreadForAI({
        spreadName,
        spreadType,
        cards: drawnCards,
        question: userQuestion,
        notes: notes,
        cardCount,
        spreadInterpretations
      });
      
      console.log('Generating AI explanation for:', spreadData);
      
      // Generate AI explanation
      const aiResponse = await aiService.generateSpreadExplanation(spreadData, currentLanguage);
      setAiExplanation(aiResponse.explanation);
      
      console.log('AI explanation generated:', aiResponse.explanation);
    } catch (error) {
      console.error('Error generating AI explanation:', error);
      setAiError(error.message || 'Failed to generate AI explanation. Please try again.');
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const saveReadingWithAI = async () => {
    // First generate AI explanation, then save
    await generateAIExplanation();
    // Save will happen automatically after AI explanation is generated
    setTimeout(() => {
      saveReading();
    }, 1000); // Small delay to ensure AI explanation is set
  };

  // Payment functions
  const handlePayForExplanation = () => {
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async (successMessage) => {
    console.log('Payment successful, message:', successMessage);
    
    // Save the reading to diary immediately after successful payment
    try {
      console.log('Saving reading after successful payment...');
      
      // Create reading data with payment status - NO AI explanation yet
      const readingData = {
        spread: spreadName,
        spreadType: spreadType,
        question: userQuestion || '',
        notes: notes || '', // Keep original notes unchanged
        cards: drawnCards.map((card, index) => {
          const translatedCard = translateCard(card);
          return {
            card: {
              name: translatedCard.name,
              meaning: translatedCard.meaning,
              position: translatedCard.position, // This is the upright/reversed status
              image: card.image,
              link: translatedCard.link
            },
            position: spreadInterpretations[index] || `${t('spreadDrawing.cardPosition')} ${index + 1}` // This is the spread position name
          };
        }),
        paymentStatus: 'completed',
        isPremium: true
      };

      console.log('Reading data to save:', readingData);
      
      // Save reading first
      const result = await saveReadingMutation.mutateAsync(readingData);
      console.log('Reading saved successfully after payment:', result);
      
      const readingId = result?.reading?.id;
      
      if (readingId) {
        // Generate AI explanation in background after saving
        try {
          console.log('=== Starting AI explanation generation ===');
          console.log('Reading ID:', readingId);
          console.log('User authenticated:', !!localStorage.getItem('authToken'));
          
          // Format the reading data for AI service
          const spreadData = aiService.formatSpreadForAI({
            spreadName,
            spreadType,
            cards: drawnCards,
            question: userQuestion || '',
            notes: notes || '',
            cardCount: drawnCards.length,
            spreadInterpretations: spreadInterpretations || []
          });
          
          console.log('Formatted spread data for AI:', JSON.stringify(spreadData, null, 2));
          
          console.log('Calling AI service...');
          const aiResponse = await aiService.generateSpreadExplanation(spreadData, currentLanguage);
          console.log('AI Response received:', aiResponse);
          
          if (aiResponse && aiResponse.explanation) {
            // Update the saved reading with AI explanation
            console.log('Updating reading with AI explanation, reading ID:', readingId);
            console.log('AI explanation content:', aiResponse.explanation);
            
            const updateResult = await updateReadingMutation.mutateAsync({
              id: readingId,
              data: { aiExplanation: aiResponse.explanation }
            });
            console.log('Update result:', updateResult);
            console.log('✅ AI explanation successfully added to reading:', readingId);
            
            // Invalidate React Query cache to ensure fresh data is loaded
            console.log('🔄 Invalidating React Query cache for reading:', readingId);
            
            // Small delay to ensure database has fully processed the update
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Invalidate and refetch the specific reading data
            await queryClient.invalidateQueries({
              queryKey: DIARY_QUERY_KEYS.reading(readingId),
              refetchType: 'active' // Only refetch if the query is currently active
            });
            
            // Also invalidate the readings list to show updated data in diary
            await queryClient.invalidateQueries({
              queryKey: ['diary-readings'],
              refetchType: 'active'
            });
            console.log('✅ Cache invalidated and refetched successfully');
          } else {
            console.warn('❌ AI response missing explanation field:', aiResponse);
          }
        } catch (aiError) {
          console.error('❌ Failed to generate AI explanation:', aiError);
          console.error('Error details:', {
            message: aiError.message,
            stack: aiError.stack,
            name: aiError.name
          });
          // Don't fail the entire process if AI generation fails
        }
        
        return { readingId };
      }
      
      return {};
      
    } catch (error) {
      console.error('Error saving reading after payment:', error);
      alert(t('spreadDrawing.paymentSuccess'));
      return {};
    }
  };

  const goBack = () => {
    navigate(returnPath, { replace: true });
  };

  // Function to save spread state before authentication
  const saveSpreadStateForAuth = () => {
    const spreadState = {
      spreadName,
      spreadType,
      cardCount,
      spreadInterpretations,
      returnPath,
      spreadConfig,
      drawnCards,
      drawnCardNames: Array.from(drawnCardNames), // Convert Set to Array for JSON serialization
      userQuestion,
      notes,
      showResults: true, // Ensure we show results when returning
      aiExplanation,
      timestamp: Date.now()
    };
    
    console.log('Saving spread state for auth:', spreadState);
    sessionStorage.setItem('pendingSpreadState', JSON.stringify(spreadState));
    sessionStorage.setItem('authRedirectPath', window.location.pathname + window.location.search);
  };

  // Clean up state after authentication (when user is authenticated and results are showing)
  useEffect(() => {
    if (isAuthenticated && showResults) {
      const pendingState = sessionStorage.getItem('pendingSpreadState');
      const redirectPath = sessionStorage.getItem('authRedirectPath');
      
      if (pendingState && redirectPath) {
        const currentPath = window.location.pathname + window.location.search;
        console.log('Authenticated user with results - cleaning up stored state');
        console.log('Current path:', currentPath, 'Expected path:', redirectPath);
        
        if (currentPath === redirectPath) {
          sessionStorage.removeItem('pendingSpreadState');
          sessionStorage.removeItem('authRedirectPath');
          console.log('Cleaned up authentication state storage');
          
          // Scroll to top to show the results
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 100);
        }
      }
    }
  }, [isAuthenticated, showResults]);

  return (
    <>
      <style>{`
        /* Card Draw Section Styles */
        .card-draw-section {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .card-circle-container {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .card-circle {
          position: relative;
          width: 700px;
          height: 700px;
          margin: 2rem 0;
          margin-top: 400px;
          cursor: grab;
          user-select: none;
          touch-action: none;
        }

        .card-circle:active {
          cursor: grabbing;
        }

        .card-circle.dragging {
          cursor: grabbing;
        }

        .circle-card {
          position: absolute;
          width: 50px;
          height: 100px;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 10;
          left: 50%;
          top: 50%;
          opacity: 1;
          transform: translate(calc(-50% + var(--card-x)), calc(-50% + var(--card-y))) rotate(var(--card-angle)) scale(1);
        }

        .circle-card.initial-load {
          animation: cardFadeIn 0.5s ease-out forwards;
        }

        @keyframes cardFadeIn {
          from {
            opacity: 0;
            transform: translate(calc(-50% + var(--card-x)), calc(-50% + var(--card-y))) rotate(var(--card-angle)) scale(0.8);
          }
          to {
            opacity: 1;
            transform: translate(calc(-50% + var(--card-x)), calc(-50% + var(--card-y))) rotate(var(--card-angle)) scale(1);
          }
        }

        @keyframes sparkle {
          0%, 100% {
            opacity: 1;
            transform: rotate(0deg) scale(1);
          }
          50% {
            opacity: 0.7;
            transform: rotate(180deg) scale(1.1);
          }
        }

        .sparkle-animation {
          animation: sparkle 1.5s ease-in-out infinite;
        }

        .circle-card:hover {
          transform: translate(calc(-50% + var(--card-x)), calc(-50% + var(--card-y))) rotate(var(--card-angle)) scale(1.1) !important;
          z-index: 20;
        }

        .circle-card.preview {
          transform: translate(calc(-50% + var(--card-x)), calc(-50% + var(--card-y))) rotate(var(--card-angle)) scale(1.2) !important;
          z-index: 25;
        }

        .circle-card.selected {
          transform: translate(calc(-50% + var(--card-x)), calc(-50% + var(--card-y))) rotate(var(--card-angle)) scale(1.3) !important;
          z-index: 30;
        }

        .circle-card.unavailable {
          opacity: 0.3;
          pointer-events: none;
          filter: grayscale(100%);
        }

        .circle-card.unavailable:hover {
          transform: translate(calc(-50% + var(--card-x)), calc(-50% + var(--card-y))) rotate(var(--card-angle)) scale(1.0) !important;
        }

        .card-back-design {
          position: relative;
          width: 100%;
          height: 100%;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          border: 2px solid rgba(255, 215, 0, 0.3);
          transition: all 0.3s ease;
        }

        .card-back-design.reversed {
          transform: rotate(180deg);
        }

        .card-back-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 6px;
        }

        .selection-indicator {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 1.5rem;
          animation: pulse 1s infinite;
          color: #ffd700;
          text-shadow: 0 0 10px rgba(255, 215, 0, 0.8);
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.7; transform: translate(-50%, -50%) scale(1.2); }
        }

        .rotation-controls {
          display: flex;
          gap: 1rem;
          margin-top: 2rem;
        }

        .rotation-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background: rgba(255, 215, 0, 0.2);
          border: 2px solid rgba(255, 215, 0, 0.4);
          color: #ffd700;
          border-radius: 25px;
          cursor: pointer;
          font-size: 0.9rem;
          transition: all 0.3s ease;
        }

        .rotation-btn:hover {
          background: rgba(255, 215, 0, 0.3);
          border-color: rgba(255, 215, 0, 0.6);
          transform: scale(1.05);
        }

        .rotation-btn:active {
          transform: scale(0.95);
        }

        .rotation-btn.pressing {
          background: rgba(255, 215, 0, 0.4);
          border-color: rgba(255, 215, 0, 0.8);
        }

        @media (max-width: 768px) {
          .card-draw-section {
            padding: 1rem;
          }

          .card-circle {
            width: 600px;
            height: 600px;
          }
          
          .circle-card {
            width: 40px;
            height: 80px;
          }
          
          /* Mobile results container fixes */
          .results-container {
            padding: 1rem 0 !important;
            overflow-x: hidden !important;
          }
          
          .results-section {
            margin: 5rem 1rem 1rem 1rem !important;
            padding: 1.5rem 1rem 3rem 1rem !important;
            max-width: calc(100vw - 2rem) !important;
            box-sizing: border-box;
          }
          
          .spread-overview {
            padding: 1.5rem 1rem !important;
            margin-bottom: 1.5rem !important;
          }
          
          .spread-layout {
            max-width: 100% !important;
            gap: 0.8rem !important;
            grid-template-columns: 1fr !important;
            grid-template-rows: none !important;
          }
          
          /* Force single column for all cards grids on mobile with highest specificity */
          .cards-grid,
          .cards-grid:has(.reading-card:nth-child(1):nth-last-child(1)),
          .cards-grid:has(.reading-card:nth-child(2):nth-last-child(1)),
          .cards-grid:has(.reading-card:nth-child(3):nth-last-child(1)),
          .cards-grid:has(.reading-card:nth-child(4):nth-last-child(1)),
          .cards-grid:has(.reading-card:nth-child(5):nth-last-child(1)),
          .cards-grid:has(.reading-card:nth-child(6):nth-last-child(1)),
          .cards-grid:has(.reading-card:nth-child(7):nth-last-child(1)) {
            gap: 1rem !important;
            grid-template-columns: 1fr !important;
            grid-template-rows: none !important;
            margin-bottom: 1.5rem !important;
          }
          
          .reading-card {
            padding: 1rem !important;
          }
          
          .question-display {
            padding: 1rem !important;
            margin-bottom: 1.5rem !important;
          }
          
          .notes-section {
            margin-bottom: 1.5rem !important;
          }
          
          .action-buttons {
            flex-direction: column !important;
            align-items: center !important;
            gap: 1rem !important;
            padding: 0 1rem !important;
          }
          
          .action-buttons button {
            width: 100% !important;
            max-width: none !important;
            justify-content: center !important;
            padding: 0.875rem 1.5rem !important;
          }
        }
        
        @media (max-width: 480px) {
          .card-circle {
            width: 480px;
            height: 480px;
          }
          
          .circle-card {
            width: 35px;
            height: 70px;
          }
          
          .rotation-btn {
            padding: 0.5rem 0.75rem;
            font-size: 0.8rem;
          }
          
          /* Extra small mobile fixes */
          .results-section {
            margin: 5rem 0.5rem 1rem 0.5rem !important;
            padding: 1rem 0.8rem 2.5rem 0.8rem !important;
            max-width: calc(100vw - 1rem) !important;
          }
          
          .spread-overview {
            padding: 1rem 0.8rem !important;
          }
          
          .spread-layout {
            gap: 0.6rem !important;
          }
          
          .spread-card-mini img {
            width: 60px !important;
          }
          
          .reading-card {
            padding: 0.8rem !important;
          }
          
          .card-image-container img {
            width: 100px !important;
          }
          
          .question-display {
            padding: 0.8rem !important;
          }
          
          h2 {
            fontSize: 1.5rem !important;
          }
          
          h3 {
            fontSize: 1.1rem !important;
          }
        }
      `}</style>
      <div 
      className="spread-drawing-page"
      style={{
        minHeight: '100vh',
        backgroundImage: window.innerWidth <= 768 ? `url(/images/bg_taroca.jpg)` : `url(/images/bg_taroca_desktop.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'top center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      {!showResults ? (
        <div className="card-draw-section" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '2rem 0',
          color: 'white',
          minHeight: '100vh',
          width: window.innerWidth > 768 ? '50%' : '100%',
          margin: '0',
          boxSizing: 'border-box',
          position: window.innerWidth > 768 ? 'fixed' : 'relative',
          left: window.innerWidth > 768 ? '0' : 'auto',
          top: window.innerWidth > 768 ? '0' : 'auto',
          overflowY: window.innerWidth > 768 ? 'auto' : 'visible'
        }}>
            {/* Back Button - Top Left Corner */}
            <button 
              className="back-btn" 
              onClick={goBack}
              style={{
                position: 'absolute',
                top: '1rem',
                left: window.innerWidth > 768 ? '2rem' : '1rem',
                background: 'rgba(0,0,0,0.4)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '50%',
                width: '48px',
                height: '48px',
                color: '#FAF8F5',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                textShadow: '1px 1px 2px rgba(0,0,0,0.6)',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                padding: '0'
              }}
              onMouseOver={(e) => {
                e.target.style.background = 'rgba(0,0,0,0.6)';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'rgba(0,0,0,0.4)';
                e.target.style.transform = 'scale(1)';
              }}
            >
              <ArrowLeft size={20} />
            </button>

            {/* Taroca Logo */}
            <img 
              src="/images/logo_taroca_white.png"
              alt="Taroca Logo"
              onClick={() => navigate('/')}
              style={{
                position: 'absolute',
                top: '1.2rem',
                left: window.innerWidth > 768 ? '5rem' : '50%',
                transform: window.innerWidth > 768 ? 'none' : 'translateX(-50%)',
                height: '40px',
                width: 'auto',
                zIndex: 1000,
                opacity: 0.9,
                cursor: 'pointer'
              }}
            />

            {/* Progress Header and Title */}
            <div className="drawing-header" style={{
              background: 'rgba(0,0,0,0.15)',
              backdropFilter: 'blur(10px)',
              padding: '1.5rem',
              margin: '5rem 0 1rem 0', // Add top margin for back button spacing
              borderRadius: '20px',
              width: '100%', // Match card-circle-container full width
              maxWidth: 'none', // Remove width constraint
              textAlign: 'center',
              border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              color: '#FAF8F5',
              position: 'relative',
              boxSizing: 'border-box' // Include padding in width calculation
            }}>
              
              {/* Page Title */}
              <h1 style={{
                color: '#FAF8F5', 
                fontSize: '2rem', 
                marginBottom: '1rem', 
                marginTop: '0.5rem',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.5)'
              }}>{spreadName}</h1>
              
              <div className="progress-indicator" style={{marginBottom: '1rem'}}>
                <div className="progress-bar" style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  height: '8px',
                  overflow: 'hidden',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}>
                  <div 
                    className="progress-fill"
                    style={{ 
                      width: `${(currentDrawIndex / cardCount) * 100}%`,
                      height: '100%',
                      background: 'linear-gradient(45deg, #ffd700, #ffed4e)',
                      transition: 'width 0.3s ease'
                    }}
                  />
                </div>
              </div>
              <p className="position-description" style={{
                color: '#FAF8F5', 
                fontSize: '1.1rem', 
                opacity: 0.9,
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
              }}>
                {isDrawing ? t('spreadDrawing.drawingCard') : `${t('spreadDrawing.drawCard')} ${currentDrawIndex + 1} ${t('spreadDrawing.of')} ${cardCount} ${t('spreadDrawing.for')} `}<strong>{getCurrentPosition()}</strong>
              </p>
            </div>

            {/* Card Circle Interface */}
            <div className="card-circle-container">
              <div 
                className={`card-circle ${isDragging ? 'dragging' : ''}`}
                onMouseDown={handleStart}
                onTouchStart={handleStart}
              >
                {randomizedCards.map((randomizedCard, circleIndex) => {
                  // Skip rendering cards that have been drawn
                  if (drawnCardIndices.has(circleIndex)) {
                    return null;
                  }
                  
                  // Apply rotation offset to each card's position (exactly like DailyCardPage)
                  // Use the current array length for angle calculation so cards redistribute smoothly
                  const baseAngle = (circleIndex * 360) / randomizedCards.length;
                  const angle = baseAngle + cardRotationOffset;
                  
                  // Responsive radius based on screen size (exactly like DailyCardPage)
                  const getRadius = () => {
                    if (window.innerWidth <= 480) return 220; // Small mobile - larger radius for 480px circle (480 * 0.45)
                    if (window.innerWidth <= 768) return 270; // Mobile - larger radius for 600px circle (600 * 0.45)
                    return 320; // Desktop
                  };
                  
                  const radius = getRadius();
                  const x = Math.cos((angle * Math.PI) / 180) * radius;
                  const y = Math.sin((angle * Math.PI) / 180) * radius;
                  
                  // Calculate card rotation (no dynamic reversal)
                  const cardRotation = angle + 90;
                  
                  // Ensure the card index is valid
                  const cardData = cardsToUse[randomizedCard.originalIndex];
                  if (!cardData) return null;

                  return (
                    <div
                      key={`${randomizedCard.originalIndex}-${circleIndex}`}
                      className={`circle-card ${selectedCardIndex === circleIndex ? 'selected' : ''} ${previewCardIndex === circleIndex ? 'preview' : ''} ${!hasInitiallyLoaded ? 'initial-load' : ''}`}
                      style={{
                        '--card-x': `${x}px`,
                        '--card-y': `${y}px`,
                        '--card-angle': `${cardRotation}deg`,
                        ...((!hasInitiallyLoaded) && { animationDelay: `${circleIndex * 0.01}s` })
                      }}
                      onClick={(e) => selectCard(circleIndex, e)}
                    >
                      <div className="card-back-design">
                        <img 
                          src="/images/card_back.jpg" 
                          alt="Tarot Card Back" 
                          className="card-back-image"
                        />
                        {selectedCardIndex === circleIndex && (
                          <div className="selection-indicator">
                            ✨
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Draw All Cards Button */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                {/* Draw All Cards Button */}
                <button 
                  className="draw-card-btn"
                  onClick={drawAllCards}
                  disabled={isDrawing || showResults}
                  style={{
                    opacity: isDrawing || showResults ? 0.6 : 1,
                    cursor: isDrawing || showResults ? 'not-allowed' : 'pointer',
                    minWidth: window.innerWidth <= 768 ? '180px' : '200px',
                    justifyContent: 'center',
                    fontSize: window.innerWidth <= 768 ? '1rem' : '1.1rem',
                    padding: window.innerWidth <= 768 ? '12px 20px' : '15px 30px',
                    marginTop: window.innerWidth <= 768 ? '-200px' : '-150px'
                  }}
                >
                  <Sparkles size={window.innerWidth <= 768 ? 18 : 20} />
                  {isDrawing ? t('spreadDrawing.drawingCards') || 'Drawing Cards...' : t('spreadDrawing.drawAllCards') || 'TAROCA Draws for You'}
                </button>
              </div>
            </div>
          </div>
      ) : (
        <div className="results-container" style={{
          minHeight: '100vh',
          maxHeight: window.innerWidth > 768 ? '100vh' : 'none',
          padding: window.innerWidth <= 768 ? '1rem 0' : '2rem 0',
          width: window.innerWidth > 768 ? '50%' : '100%',
          margin: '0',
          boxSizing: 'border-box',
          position: window.innerWidth > 768 ? 'fixed' : 'relative',
          left: window.innerWidth > 768 ? '0' : 'auto',
          top: window.innerWidth > 768 ? '0' : 'auto',
          overflowY: window.innerWidth > 768 ? 'auto' : 'visible',
          overflowX: 'hidden'
        }}>
          {/* Back Button - Top Left Corner */}
          <button 
            className="back-btn" 
            onClick={goBack}
            style={{
              position: 'absolute',
              top: '1rem',
              left: window.innerWidth > 768 ? '2rem' : '1rem',
              background: 'rgba(0,0,0,0.4)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '50%',
              width: '48px',
              height: '48px',
              color: '#FAF8F5',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              textShadow: '1px 1px 2px rgba(0,0,0,0.6)',
              zIndex: 1000,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              padding: '0'
            }}
            onMouseOver={(e) => {
              e.target.style.background = 'rgba(0,0,0,0.6)';
              e.target.style.transform = 'scale(1.1)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'rgba(0,0,0,0.4)';
              e.target.style.transform = 'scale(1)';
            }}
          >
            <ArrowLeft size={20} />
          </button>

          {/* Taroca Logo */}
          <img 
            src="/images/logo_taroca_white.png"
            alt="Taroca Logo"
            onClick={() => navigate('/')}
            style={{
              position: 'absolute',
              top: '1.2rem',
              left: window.innerWidth > 768 ? '5rem' : '50%',
              transform: window.innerWidth > 768 ? 'none' : 'translateX(-50%)',
              height: '40px',
              width: 'auto',
              zIndex: 1000,
              opacity: 0.9,
              cursor: 'pointer'
            }}
          />
          <div className="results-section" style={{
            background: 'rgba(0,0,0,0.15)',
            backdropFilter: 'blur(10px)',
            margin: window.innerWidth > 768 ? '5rem 0 1rem 1rem' : window.innerWidth <= 480 ? '5rem 0.5rem 1rem 0.5rem' : '5rem 1rem 1rem 1rem',
            padding: window.innerWidth <= 480 ? '1rem 0.8rem 2.5rem 0.8rem' : window.innerWidth <= 768 ? '1.5rem 1rem 3rem 1rem' : '2rem 2rem 4rem 2rem',
            borderRadius: '20px',
            maxWidth: window.innerWidth > 768 ? 'calc(100% - 2rem)' : window.innerWidth <= 480 ? 'calc(100vw - 1rem)' : 'calc(100vw - 2rem)',
            border: '1px solid rgba(255,255,255,0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            color: '#FAF8F5',
            boxSizing: 'border-box'
          }}>
          <h2 style={{
            color: '#FAF8F5', 
            textAlign: 'center', 
            marginBottom: '2rem',
            fontSize: window.innerWidth <= 480 ? '1.5rem' : window.innerWidth <= 768 ? '1.7rem' : '2rem',
            textShadow: '2px 2px 4px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.5)'
          }}>{spreadName}</h2>
          
          {/* Your Question Section */}
          {userQuestion && (
            <div style={{
              marginBottom: '2rem',
              padding: '1.5rem',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(5px)',
              borderRadius: '15px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{
                color: '#FAF8F5',
                marginBottom: '1rem',
                fontSize: window.innerWidth <= 480 ? '1.1rem' : window.innerWidth <= 768 ? '1.2rem' : '1.3rem',
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                textAlign: 'center'
              }}>{t('spreadDrawing.yourQuestion')}</h3>
              <p style={{
                color: '#FAF8F5',
                fontStyle: 'italic',
                margin: 0,
                fontSize: window.innerWidth <= 480 ? '0.9rem' : '1rem',
                lineHeight: '1.5',
                textAlign: 'center',
                textShadow: '1px 1px 2px rgba(0,0,0,0.6)'
              }}>"{userQuestion}"</p>
            </div>
          )}
          
          {/* Spread Overview Section */}
          <div className="spread-overview" style={{
            background: 'rgba(0,0,0,0.1)',
            backdropFilter: 'blur(5px)',
            padding: '2rem',
            borderRadius: '15px',
            marginBottom: '2rem',
            border: '1px solid rgba(255,255,255,0.15)',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{
              color: '#FAF8F5', 
              textAlign: 'center', 
              marginBottom: '1.5rem',
              fontSize: window.innerWidth <= 480 ? '1.1rem' : window.innerWidth <= 768 ? '1.2rem' : '1.3rem',
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
            }}>
              {t('spreadDrawing.spreadOverview')}
            </h3>
            
            {/* Special layout for 10-card Celtic Cross spread */}
            {cardCount === 10 ? (
              <div className="celtic-cross-layout" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 80px)',
                gap: '8px',
                justifyContent: 'center',
                alignItems: 'start',
                maxWidth: '400px',
                margin: '0 auto',
                position: 'relative'
              }}>
                {drawnCards.map((card, index) => {
                  const translatedCard = translateCard(card);
                  
                  // Multi-language reversed detection function
                  const isCardReversed = (card, translatedCard) => {
                    // Check all possible reversed indicators
                    const reversedTranslations = ['reversed', '逆位置', '逆位'];
                    
                    // Check if card name starts with "Reversed" in English
                    if (card.name.startsWith('Reversed')) {
                      return true;
                    }
                    
                    // Check translated position
                    if (translatedCard.position === t('tarot.position.reversed')) {
                      return true;
                    }
                    
                    // Check direct position match with translation strings
                    if (translatedCard.position && reversedTranslations.includes(translatedCard.position)) {
                      return true;
                    }
                    
                    return false;
                  };
                  
                  const isReversed = isCardReversed(card, translatedCard);
                  
                  // Celtic Cross positioning with improved spacing
                  const getCelticCrossPosition = (index) => {
                    switch(index) {
                      case 0: return { gridColumn: '2', gridRow: '3', paddingTop: '20px' }; // 1st card - center with top padding
                      case 1: return { gridColumn: '2', gridRow: '4', transform: 'rotate(-90deg)', zIndex: 2, paddingTop: '0px' }; // 2nd card - rotated -90 degrees at bottom of 1st card
                      case 2: return { gridColumn: '1', gridRow: '3', paddingTop: '20px' }; // 3rd card - left of center
                      case 3: return { gridColumn: '3', gridRow: '3', paddingTop: '20px' }; // 4th card - right of center
                      case 4: return { gridColumn: '2', gridRow: '2', paddingTop: '20px' }; // 5th card - above center
                      case 5: return { gridColumn: '2', gridRow: '5', paddingTop: '20px' }; // 6th card - below center (moved down)
                      case 6: return { gridColumn: '4', gridRow: '5', paddingTop: '20px' }; // 7th card - right of 6th
                      case 7: return { gridColumn: '4', gridRow: '4', paddingTop: '20px' }; // 8th card - above 7th
                      case 8: return { gridColumn: '4', gridRow: '3', paddingTop: '20px' }; // 9th card - above 8th
                      case 9: return { gridColumn: '4', gridRow: '2', paddingTop: '20px' }; // 10th card - above 9th
                      default: return { gridColumn: '1', gridRow: '1', paddingTop: '20px' };
                    }
                  };
                  
                  const gridPosition = getCelticCrossPosition(index);
                  
                  return (
                    <div key={index} className="celtic-cross-card" style={{
                      gridColumn: gridPosition.gridColumn,
                      gridRow: gridPosition.gridRow,
                      paddingTop: gridPosition.paddingTop,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      position: 'relative',
                      zIndex: gridPosition.zIndex || 1
                    }}>
                      <div style={{
                        background: 'linear-gradient(45deg, #ffd700, #ffed4e)',
                        color: '#333',
                        padding: '0.2rem 0.4rem',
                        borderRadius: '15px',
                        fontSize: '0.6rem',
                        fontWeight: 'bold',
                        marginBottom: '0.3rem',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        display: 'inline-block',
                        minWidth: 'auto',
                        whiteSpace: 'nowrap',
                        maxWidth: '70px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {spreadInterpretations[index] || `${index + 1}`}
                      </div>
                      <div style={{
                        position: 'relative',
                        display: 'inline-block'
                      }}>
                        <img 
                          src={`/images/tarot-cards/image_${getImageIndex(card)}.jpg`}
                          alt={card.name}
                          style={{
                            width: '60px',
                            height: 'auto',
                            borderRadius: '4px',
                            transform: gridPosition.transform ? 
                              (isReversed ? `${gridPosition.transform} rotate(180deg)` : gridPosition.transform) :
                              (isReversed ? 'rotate(180deg)' : 'none'),
                            border: '1px solid #ddd',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                            transition: 'transform 0.3s ease'
                          }}
                          onError={(e) => {
                            console.log('Image failed to load:', e.target.src, 'for card:', translatedCard.name, 'originalIndex:', card.originalIndex);
                            e.target.src = '/images/card_back.jpg';
                          }}
                        />
                        {isReversed && (
                          <div style={{
                            position: 'absolute',
                            top: '-3px',
                            right: '-3px',
                            background: '#e74c3c',
                            color: 'white',
                            borderRadius: '50%',
                            width: '14px',
                            height: '14px',
                            fontSize: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold'
                          }}>
                            R
                          </div>
                        )}
                      </div>
                      <div style={{
                        fontSize: '0.6rem',
                        color: '#FAF8F5',
                        marginTop: '0.3rem',
                        fontWeight: '500',
                        lineHeight: '1.1',
                        maxWidth: '70px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                      }}>
                        {translatedCard.name.replace(/^Reversed /, '')}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Regular grid layout for other spreads */
              <div className="spread-layout" style={{
                display: 'grid',
                gap: window.innerWidth <= 768 ? '0.8rem' : '1rem',
                justifyContent: 'center',
                alignItems: 'center',
                gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 
                                   cardCount === 1 ? '1fr' : 
                                   cardCount === 2 ? 'repeat(2, 1fr)' :
                                   cardCount === 4 ? 'repeat(2, 1fr)' :
                                   cardCount === 6 ? 'repeat(3, 1fr)' :
                                   'repeat(auto-fit, minmax(80px, 1fr))',
                maxWidth: window.innerWidth <= 768 ? '100%' : '600px',
                margin: '0 auto'
              }}>
                {drawnCards.map((card, index) => {
                  const translatedCard = translateCard(card);
                  
                  // Multi-language reversed detection function
                  const isCardReversed = (card, translatedCard) => {
                    // Check all possible reversed indicators
                    const reversedTranslations = ['reversed', '逆位置', '逆位'];
                    
                    // Check if card name starts with "Reversed" in English
                    if (card.name.startsWith('Reversed')) {
                      return true;
                    }
                    
                    // Check translated position
                    if (translatedCard.position === t('tarot.position.reversed')) {
                      return true;
                    }
                    
                    // Check direct position match with translation strings
                    if (translatedCard.position && reversedTranslations.includes(translatedCard.position)) {
                      return true;
                    }
                    
                    return false;
                  };
                  
                  const isReversed = isCardReversed(card, translatedCard);
                  
                  return (
                  <div key={index} className="spread-card-mini" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    position: 'relative'
                  }}>
                    <div style={{
                      background: 'linear-gradient(45deg, #ffd700, #ffed4e)',
                      color: '#333',
                      padding: '0.3rem 0.6rem',
                      borderRadius: '20px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      marginBottom: '0.5rem',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      display: 'inline-block',
                      minWidth: 'auto',
                      whiteSpace: 'nowrap'
                    }}>
                      {spreadInterpretations[index] || `${index + 1}`}
                    </div>
                    <div style={{
                      position: 'relative',
                      display: 'inline-block'
                    }}>
                    <img 
                      src={`/images/tarot-cards/image_${getImageIndex(card)}.jpg`}
                      alt={card.name}
                      style={{
                        width: window.innerWidth <= 480 ? '60px' : '80px',
                        height: 'auto',
                        borderRadius: '6px',
                        transform: isReversed ? 'rotate(180deg)' : 'none',
                        border: '1px solid #ddd',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        transition: 'transform 0.3s ease'
                      }}
                      onError={(e) => {
                        console.log('Image failed to load:', e.target.src, 'for card:', translatedCard.name, 'originalIndex:', card.originalIndex);
                        e.target.src = '/images/card_back.jpg'; // Fallback image
                      }}
                    />
                      {isReversed && (
                        <div style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          background: '#e74c3c',
                          color: 'white',
                          borderRadius: '50%',
                          width: '16px',
                          height: '16px',
                          fontSize: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold'
                        }}>
                          R
                        </div>
                      )}
                    </div>
                    <div style={{
                      fontSize: '0.7rem',
                      color: '#FAF8F5',
                      marginTop: '0.5rem',
                      fontWeight: '500',
                      lineHeight: '1.2',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                    }}>
                      {translatedCard.name}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="cards-grid">
            {drawnCards.map((card, index) => {
              const translatedCard = translateCard(card);
              return (
              <div key={index} className="reading-card" style={{
                background: 'rgba(0,0,0,0.1)',
                backdropFilter: 'blur(5px)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
              }}>
                <div className="card-position" style={{
                  background: 'linear-gradient(45deg, #ffd700, #ffed4e)',
                  color: '#333',
                  padding: '0.5rem',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  {spreadInterpretations[index] || `${t('spreadDrawing.cardPosition')} ${index + 1}`}
                </div>
                
                {/* Card Image */}
                <div className="card-image-container" style={{
                  textAlign: 'center',
                  marginBottom: '1rem'
                }}>
                  <img 
                    src={`/images/tarot-cards/image_${getImageIndex(card)}.jpg`}
                    alt={translatedCard.name}
                    style={{
                      width: window.innerWidth <= 480 ? '100px' : '120px',
                      height: 'auto',
                      borderRadius: '8px',
                      transform: translatedCard.position === t('tarot.position.reversed') ? 'rotate(180deg)' : 'none',
                      border: '2px solid #ddd',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      transition: 'transform 0.3s ease'
                    }}
                    onError={(e) => {
                      console.log('Image failed to load:', e.target.src, 'for card:', translatedCard.name, 'originalIndex:', card.originalIndex);
                      e.target.src = '/images/card_back.jpg'; // Fallback image
                    }}
                  />
                </div>
                
                <div className="card-content">
                  <h3 className="card-name" style={{
                    color: '#FAF8F5',
                    fontSize: '1.2rem',
                    marginBottom: '0.75rem',
                    textAlign: 'center',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                  }}>
                    {translatedCard.name} ({translatedCard.position})
                  </h3>
                  <p style={{
                    color: '#FAF8F5',
                    fontSize: '0.95rem',
                    lineHeight: '1.5',
                    marginBottom: '1rem',
                    opacity: 0.9,
                    textShadow: '1px 1px 2px rgba(0,0,0,0.6)'
                  }}>
                    <strong>{t('spreadDrawing.cardMeaning')}</strong> {translatedCard.meaning}
                  </p>
                  
                  {/* Card Meaning Link */}
                  {translatedCard.link && (
                    <div style={{
                      textAlign: 'center',
                      marginTop: '1rem'
                    }}>
                      <a 
                        href={translatedCard.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          color: '#C0AC93',
                          borderStyle: 'dashed',
                          borderWidth: '1px',
                          borderColor: '#C0AC93',
                          borderRadius: '0px',
                          padding: '15px 30px',
                          fontFamily: 'Jost, sans-serif',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          textDecoration: 'none',
                          background: 'transparent',
                          transition: 'all 0.3s ease',
                          boxShadow: 'none'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = 'rgba(192, 172, 147, 0.1)';
                          e.target.style.borderColor = '#261E1E';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.borderColor = '#C0AC93';
                        }}
                      >
                        {t('spreadDrawing.learnMoreCard')}
                      </a>
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>

          <div className="notes-section" style={{marginBottom: '2rem'}}>
            <h3 style={{
              color: '#FAF8F5', 
              marginBottom: '1rem',
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
            }}>{t('spreadDrawing.personalNotes')}</h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('spreadDrawing.notesPlaceholder')}
              style={{
                width: '100%',
                padding: '1rem',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'inherit',
                resize: 'vertical',
                minHeight: '100px',
                background: 'rgba(0,0,0,0.1)',
                backdropFilter: 'blur(5px)',
                color: '#FAF8F5',
                boxSizing: 'border-box'
              }}
              rows="4"
            />
          </div>

          {/* AI Explanation Section */}
          <div className="ai-explanation-section" style={{marginBottom: '2rem'}}>
            <h3 style={{
              color: '#FAF8F5', 
              marginBottom: '1rem',
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Sparkles size={20} style={{color: '#ffd700'}} />
              {t('spreadDrawing.unlockExperience')}
            </h3>
            
            {isAuthenticated && !aiExplanation && !isGeneratingAI && (
              <div style={{
                padding: '1rem',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '8px',
                background: 'rgba(0,0,0,0.1)',
                backdropFilter: 'blur(5px)',
                textAlign: 'center'
              }}>
                <p style={{
                  color: '#FAF8F5',
                  marginBottom: '1rem',
                  opacity: 0.8
                }}>
                  {t('spreadDrawing.findLesson')}
                </p>
                <button 
                  onClick={handlePayForExplanation}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    background: 'linear-gradient(45deg, #9333ea, #c084fc)',
                    color: 'white',
                    borderRadius: '25px',
                    fontSize: '1rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 8px rgba(147, 51, 234, 0.3)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <Sparkles size={16} />
                  {t('spreadDrawing.getTarocaWisdom')}
                </button>
              </div>
            )}

            {isGeneratingAI && (
              <div style={{
                padding: '1.5rem',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '8px',
                background: 'rgba(0,0,0,0.1)',
                backdropFilter: 'blur(5px)',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  color: '#ffd700',
                  marginBottom: '0.5rem'
                }}>
                  <Sparkles size={20} className="sparkle-animation" />
                  <span>{t('spreadDrawing.tarocaRevealing')}</span>
                </div>
                <p style={{color: '#FAF8F5', opacity: 0.7, fontSize: '0.9rem'}}>
                  {t('spreadDrawing.interpretingSpread')}
                </p>
              </div>
            )}

            {/* Authentication Gate or AI Explanation */}
            {isAuthenticated ? (
              // Existing Taroca's Explanation section for authenticated users
              aiExplanation && (
                <div id="taroca-explanation" className="ai-explanation-section" style={{
                  color: 'white'
                }}>
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.05), rgba(184, 134, 11, 0.05))',
                    border: '1px solid rgba(212, 175, 55, 0.2)',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginTop: '0.5rem'
                  }}>
                    <div style={{color: 'white'}}>
                      <AIExplanationDisplay explanation={aiExplanation} />
                    </div>
                  </div>
                </div>
              )
            ) : (
              // Authentication Prompt for non-authenticated users
              <div className="auth-prompt-section" style={{
                background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.05), rgba(184, 134, 11, 0.05))',
                border: '1px solid rgba(212, 175, 55, 0.2)',
                borderRadius: '15px',
                padding: '2rem',
                margin: '2rem 0',
                textAlign: 'center'
              }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <span style={{ fontSize: '2rem', marginRight: '0.5rem' }}>✨</span>
                  <h3 style={{ 
                    color: '#D4AF37', 
                    fontSize: '1.4rem', 
                    fontWeight: '600',
                    margin: '0.5rem 0 1rem 0',
                    display: 'inline'
                  }}>
                    {t('spreadDrawing.unlockExperience')}
                  </h3>
                </div>
                  
                  <div style={{ color: 'rgba(255, 255, 255, 0.9)', marginBottom: '2rem' }}>
                    <p style={{ fontSize: '1.1rem', marginBottom: '1rem', lineHeight: '1.6' }}>
                      {t('spreadDrawing.signInToUnlock')}
                    </p>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                      gap: '1rem',
                      marginBottom: '1.5rem',
                      textAlign: 'left'
                    }}>
                      <div style={{ 
                        background: 'rgba(255, 255, 255, 0.05)', 
                        padding: '1rem', 
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <h4 style={{ color: '#D4AF37', marginBottom: '0.5rem', fontSize: '1rem' }}>
                          {t('spreadDrawing.tarocaExplanationTitle')}
                        </h4>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                          {t('spreadDrawing.findLesson')}
                        </p>
                      </div>
                      
                      <div style={{ 
                        background: 'rgba(255, 255, 255, 0.05)', 
                        padding: '1rem', 
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <h4 style={{ color: '#D4AF37', marginBottom: '0.5rem', fontSize: '1rem' }}>
                          {t('spreadDrawing.personalDiary')}
                        </h4>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                          {t('spreadDrawing.personalDiaryDesc')}
                        </p>
                      </div>
                      
                      <div style={{ 
                        background: 'rgba(255, 255, 255, 0.05)', 
                        padding: '1rem', 
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <h4 style={{ color: '#D4AF37', marginBottom: '0.5rem', fontSize: '1rem' }}>
                          {t('spreadDrawing.progressTracking')}
                        </h4>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                          {t('spreadDrawing.progressTrackingDesc')}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        saveSpreadStateForAuth();
                        navigate('/login', { 
                          state: { 
                            from: window.location.pathname + window.location.search,
                            message: t('spreadDrawing.signInMessage')
                          } 
                        });
                      }}
                      style={{
                        padding: '0.875rem 2rem',
                        background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        minWidth: '140px'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 8px 25px rgba(212, 175, 55, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      {t('spreadDrawing.signIn')}
                    </button>
                    
                    <button
                      onClick={() => {
                        saveSpreadStateForAuth();
                        navigate('/login?mode=register', { 
                          state: { 
                            from: window.location.pathname + window.location.search,
                            message: 'Create your account to start your tarot journey!'
                          } 
                        });
                      }}
                      style={{
                        padding: '0.875rem 2rem',
                        background: 'transparent',
                        color: '#D4AF37',
                        border: '2px solid #D4AF37',
                        borderRadius: '50px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        minWidth: '140px'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.background = 'rgba(212, 175, 55, 0.1)';
                        e.target.style.transform = 'translateY(-2px)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = 'transparent';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      {t('spreadDrawing.signUpFree')}
                    </button>
                  </div>
                  
                  <p style={{ 
                    fontSize: '0.85rem', 
                    color: 'rgba(255, 255, 255, 0.6)', 
                    marginTop: '1rem',
                    fontStyle: 'italic' 
                  }}>
                    {t('spreadDrawing.joinUsers')}
                  </p>
              </div>
            )}

            {aiError && (
              <div style={{
                padding: '1rem',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                background: 'rgba(239, 68, 68, 0.1)',
                backdropFilter: 'blur(5px)'
              }}>
                <p style={{color: '#fca5a5', fontSize: '0.9rem'}}>
                  {aiError}
                </p>
                <button 
                  onClick={generateAIExplanation}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.4rem 0.8rem',
                    border: '1px solid rgba(239, 68, 68, 0.5)',
                    background: 'rgba(239, 68, 68, 0.2)',
                    color: '#fca5a5',
                    borderRadius: '15px',
                    fontSize: '0.8rem',
                    cursor: 'pointer'
                  }}
                >
                  {t('spreadDrawing.tryAgain')}
                </button>
              </div>
            )}
          </div>

          <div className="action-buttons" style={{
            display: 'flex',
            gap: window.innerWidth <= 768 ? '1rem' : '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap',
            padding: window.innerWidth <= 768 ? '0 1rem' : '0'
          }}>
            <button 
              onClick={resetSpread} 
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                border: '2px solid #ffd700',
                background: 'white',
                color: '#333',
                borderRadius: '25px',
                fontSize: '1rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#ffd700';
                e.target.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'white';
                e.target.style.color = '#333';
              }}
            >
              <RefreshCw size={18} />
              {t('spreadDrawing.newReading')}
            </button>
            
            {/* Conditional rendering based on authentication status */}
            {isAuthenticated && (
              <button 
                onClick={saveReading}
                disabled={saveReadingMutation.isPending}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  background: saveReadingMutation.isPending 
                    ? 'linear-gradient(45deg, #ccc, #ddd)' 
                    : 'linear-gradient(45deg, #ffd700, #ffed4e)',
                  color: '#333',
                  borderRadius: '25px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: saveReadingMutation.isPending ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  opacity: saveReadingMutation.isPending ? 0.7 : 1
                }}
                onMouseOver={(e) => {
                  if (!saveReadingMutation.isPending) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.15)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!saveReadingMutation.isPending) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  }
                }}
              >
                <BookOpen size={18} />
                {saveReadingMutation.isPending ? t('spreadDrawing.saving') : t('spreadDrawing.saveToDiary')}
              </button>
            )}
          </div>
        </div>
        </div>
      )}
    </div>
    
    {/* Payment Modal */}
    <PaymentModal
      isOpen={showPaymentModal}
      onClose={() => setShowPaymentModal(false)}
      spreadData={{
        spreadName,
        spreadType,
        cards: drawnCards,
        question: userQuestion,
        notes: notes,
        cardCount,
        spreadInterpretations
      }}
      onSuccess={handlePaymentSuccess}
    />
    </>
  );
};

export default SpreadDrawingPage;
